// MainApp.js

import React, { useState, useRef, useEffect } from 'react';
import './MainApp.css';

// Import components
import TopMenuBar from '../TopMenuBar/TopMenuBar';
import Sidebar from '../Sidebar/Sidebar';
import CanvasArea from '../CanvasArea/CanvasArea';
import ToolPropertiesPanel from '../ToolPropertiesPanel/ToolPropertiesPanel';
import PageTabs from '../PageTabs/PageTabs';
import DeleteModal from '../DeleteModal/DeleteModal';
import LastPageModal from '../LastPageModal/LastPageModal';
import ImageLibraryModal from '../ImageLibraryModal/ImageLibraryModal';
import UploadTemplateModal from '../UploadTemplateModal/UploadTemplateModal';
import AdvancedPDFExportModal from '../AdvancedPDFExportModal/AdvancedPDFExportModal';
import Walkthrough from '../Walkthrough/Walkthrough';
import jsPDF from 'jspdf';
import Konva from 'konva';
import { createKonvaShape } from '../../utils/createKonvaShape';
import { useLocation } from 'react-router-dom';
import '../../Styles/fonts.css'; // Adjust the path based on your actual file structure
import { preloadAppFonts } from '../../utils/fontPreloader';
// Import background images
import calmImage from '../../assets/Images/calm.webp';
import creativeImage from '../../assets/Images/creative.webp';
import energeticImage from '../../assets/Images/energetic.webp';
import craftyImage from '../../assets/Images/crafty.webp';
import workbenchImage from '../../assets/Images/workbench.webp';
import cuteImage from '../../assets/Images/Cute.webp';
import hungryImage from '../../assets/Images/Hungry.webp';
import zenImage from '../../assets/Images/Zen.webp';
import eduGrooveImage from '../../assets/Images/edugroovedefault.webp';
import { v4 as uuidv4 } from 'uuid';
import axios from 'axios';
import LottieLoader from '../LoadingSpinner/LottieLoader';
import { useContext } from 'react';
import { UserContext } from '../../context/UserContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faArrowRight,
  faTrash,
  faCopy,
  faPaste,
  faArrowUp,
  faArrowDown,
} from '@fortawesome/free-solid-svg-icons';

const shuffleArray = (array) => {
  const copy = [...array];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
};

const generateMazeWalls = (
  width,
  height,
  rows = 8,
  columns = 8,
  exits = 2,
  complexity = 3
) => {
  const safeRows = Math.max(1, parseInt(rows, 10) || 1);
  const safeColumns = Math.max(1, parseInt(columns, 10) || 1);
  const safeExits = Math.min(4, Math.max(1, parseInt(exits, 10) || 1));
  const safeComplexity = Math.min(5, Math.max(1, parseInt(complexity, 10) || 1));

  const cellWidth = width / safeColumns;
  const cellHeight = height / safeRows;

  const grid = Array.from({ length: safeRows }, (_, row) =>
    Array.from({ length: safeColumns }, (_, col) => ({
      row,
      col,
      visited: false,
      walls: {
        top: true,
        right: true,
        bottom: true,
        left: true,
      },
    }))
  );

  const directions = [
    { dr: -1, dc: 0, wall: 'top', opposite: 'bottom' },
    { dr: 0, dc: 1, wall: 'right', opposite: 'left' },
    { dr: 1, dc: 0, wall: 'bottom', opposite: 'top' },
    { dr: 0, dc: -1, wall: 'left', opposite: 'right' },
  ];

  const stack = [];
  const startCell = grid[0][0];
  startCell.visited = true;
  stack.push(startCell);

  while (stack.length > 0) {
    const current = stack[stack.length - 1];

    const neighbors = shuffleArray(directions).filter(({ dr, dc }) => {
      const nextRow = current.row + dr;
      const nextCol = current.col + dc;

      return (
        nextRow >= 0 &&
        nextRow < safeRows &&
        nextCol >= 0 &&
        nextCol < safeColumns &&
        !grid[nextRow][nextCol].visited
      );
    });

    if (neighbors.length === 0) {
      stack.pop();
      continue;
    }

    const nextDirection = neighbors[0];
    const nextRow = current.row + nextDirection.dr;
    const nextCol = current.col + nextDirection.dc;
    const nextCell = grid[nextRow][nextCol];

    current.walls[nextDirection.wall] = false;
    nextCell.walls[nextDirection.opposite] = false;
    nextCell.visited = true;
    stack.push(nextCell);
  }

  const complexityRatios = {
    1: 18,
    2: 12,
    3: 8,
    4: 6,
    5: 4,
  };

  const divisor = complexityRatios[safeComplexity] || 8;

  const extraOpeningsCount = Math.max(
    1,
    Math.floor((safeRows * safeColumns) / divisor)
  );

  const internalWallCandidates = [];

  for (let row = 0; row < safeRows; row++) {
    for (let col = 0; col < safeColumns; col++) {
      if (col < safeColumns - 1) {
        internalWallCandidates.push({
          row,
          col,
          wall: 'right',
          oppositeRow: row,
          oppositeCol: col + 1,
          oppositeWall: 'left',
        });
      }

      if (row < safeRows - 1) {
        internalWallCandidates.push({
          row,
          col,
          wall: 'bottom',
          oppositeRow: row + 1,
          oppositeCol: col,
          oppositeWall: 'top',
        });
      }
    }
  }

  shuffleArray(internalWallCandidates)
    .slice(0, extraOpeningsCount)
    .forEach(({ row, col, wall, oppositeRow, oppositeCol, oppositeWall }) => {
      grid[row][col].walls[wall] = false;
      grid[oppositeRow][oppositeCol].walls[oppositeWall] = false;
    });

  const exitCandidates = [];

  for (let col = 0; col < safeColumns; col++) {
    exitCandidates.push({ row: 0, col, side: 'top' });
    exitCandidates.push({ row: safeRows - 1, col, side: 'bottom' });
  }

  for (let row = 0; row < safeRows; row++) {
    exitCandidates.push({ row, col: 0, side: 'left' });
    exitCandidates.push({ row, col: safeColumns - 1, side: 'right' });
  }

  const chosenExits = shuffleArray(exitCandidates).slice(0, safeExits);

  chosenExits.forEach(({ row, col, side }) => {
    grid[row][col].walls[side] = false;
  });

  const wallSegments = [];

  for (let row = 0; row < safeRows; row++) {
    for (let col = 0; col < safeColumns; col++) {
      const cell = grid[row][col];
      const x = col * cellWidth;
      const y = row * cellHeight;

      if (cell.walls.top) {
        wallSegments.push([x, y, x + cellWidth, y]);
      }

      if (cell.walls.left) {
        wallSegments.push([x, y, x, y + cellHeight]);
      }

      if (row === safeRows - 1 && cell.walls.bottom) {
        wallSegments.push([x, y + cellHeight, x + cellWidth, y + cellHeight]);
      }

      if (col === safeColumns - 1 && cell.walls.right) {
        wallSegments.push([x + cellWidth, y, x + cellWidth, y + cellHeight]);
      }
    }
  }

  return wallSegments;
};

const getPdfJsLib = () => {
  const lib = window.pdfjsLib;

  if (!lib) {
    throw new Error('PDF library not loaded.');
  }

  if (lib.GlobalWorkerOptions) {
    lib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';
  }

  return lib;
};

const flatPointsToPairs = (flatPoints = []) => {
  const pairs = [];
  for (let i = 0; i < flatPoints.length; i += 2) {
    pairs.push({ x: flatPoints[i], y: flatPoints[i + 1] });
  }
  return pairs;
};

const pairsToFlatPoints = (pairs = []) => {
  return pairs.flatMap((p) => [p.x, p.y]);
};

const simplifyFlatPoints = (flatPoints = [], minDistance = 2) => {
  if (!flatPoints || flatPoints.length <= 4) return flatPoints || [];

  const simplified = [flatPoints[0], flatPoints[1]];
  let lastX = flatPoints[0];
  let lastY = flatPoints[1];

  for (let i = 2; i < flatPoints.length - 2; i += 2) {
    const x = flatPoints[i];
    const y = flatPoints[i + 1];
    const dx = x - lastX;
    const dy = y - lastY;

    if (Math.hypot(dx, dy) >= minDistance) {
      simplified.push(x, y);
      lastX = x;
      lastY = y;
    }
  }

  const finalX = flatPoints[flatPoints.length - 2];
  const finalY = flatPoints[flatPoints.length - 1];

  if (
    simplified[simplified.length - 2] !== finalX ||
    simplified[simplified.length - 1] !== finalY
  ) {
    simplified.push(finalX, finalY);
  }

  return simplified;
};

const chaikinSmoothFlatPoints = (flatPoints = [], passes = 1) => {
  if (!flatPoints || flatPoints.length <= 4 || passes <= 0) {
    return flatPoints || [];
  }

  let pairs = flatPointsToPairs(flatPoints);

  for (let pass = 0; pass < passes; pass++) {
    if (pairs.length <= 2) break;

    const next = [pairs[0]];

    for (let i = 0; i < pairs.length - 1; i++) {
      const p0 = pairs[i];
      const p1 = pairs[i + 1];

      const q = {
        x: 0.75 * p0.x + 0.25 * p1.x,
        y: 0.75 * p0.y + 0.25 * p1.y,
      };

      const r = {
        x: 0.25 * p0.x + 0.75 * p1.x,
        y: 0.25 * p0.y + 0.75 * p1.y,
      };

      next.push(q, r);
    }

    next.push(pairs[pairs.length - 1]);
    pairs = next;
  }

  return pairsToFlatPoints(pairs);
};

const buildSmoothedPencilPoints = (rawPoints = [], smoothness = 0.5, zoomScale = 1) => {
  if (!rawPoints || rawPoints.length <= 4) return rawPoints || [];

  const safeScale = Math.max(zoomScale || 1, 0.01);
  const clampedSmoothness = Math.max(0, smoothness || 0);

  const minDistance =
    (5 + clampedSmoothness * 4) / safeScale;

  const simplified = simplifyFlatPoints(rawPoints, minDistance);

  const passes = 4 + Math.round(clampedSmoothness * 3);

  return chaikinSmoothFlatPoints(simplified, passes);
};

const buildGridCellFills = (rows, columns, defaultColor = '#ffffff') => {
  const safeRows = Math.max(1, parseInt(rows, 10) || 1);
  const safeColumns = Math.max(1, parseInt(columns, 10) || 1);
  return Array.from(
    { length: safeRows * safeColumns },
    () => defaultColor
  );
};

const getFittedImageSize = (naturalWidth, naturalHeight, maxSize = 300) => {
  const safeWidth = naturalWidth || maxSize;
  const safeHeight = naturalHeight || maxSize;

  const ratio = Math.min(maxSize / safeWidth, maxSize / safeHeight, 1);

  return {
    width: Math.round(safeWidth * ratio),
    height: Math.round(safeHeight * ratio),
  };
};

const MainApp = () => {
  const location = useLocation();
  const initialState = location.state?.initialState;
  const incomingTemplate = location.state?.editingTemplate;
  const [templates, setTemplates] = useState([]);
  // State variables

  const { currentUser } = useContext(UserContext);
  const tourStorageUserId = currentUser?.id || 'guest';

  const tourCompletedStorageKey =
    `edugroove_walkthrough_completed_${tourStorageUserId}`;

  const editorTourSteps = [
    {
      selector: '[data-tour="editor-sidebar"]',
      placement: 'right',
      eyebrow: 'Creation Tools',
      title: 'Add content from the toolbar',
      description: (
        <p>
          Use the toolbar to select, draw, add shapes, create text,
          insert images and use the other worksheet tools.
        </p>
      ),
      onEnter: () => {
        setIsPropertiesCollapsed(false);
        handleToolClick('select');
      },
    },
    {
      selector: '[data-tour="editor-workspace"]',
      placement: 'center',
      eyebrow: 'Worksheet',
      title: 'Build your worksheet on the canvas',
      description: (
        <p>
          The white page is the printable worksheet. The surrounding
          area is your workspace and will not appear in the exported
          worksheet.
        </p>
      ),
    },
    {
      selector: '[data-tour="editor-properties"]',
      placement: 'left',
      eyebrow: 'Editing',
      title: 'Change settings in the properties panel',
      description: (
        <p>
          This panel changes depending on the selected tool or object.
          Use it to control colours, sizes, grids, text and other
          settings.
        </p>
      ),
      onEnter: () => {
        setIsPropertiesCollapsed(false);
        handleToolClick('shape');
      },
    },
    {
      selectors: [
        '[data-tour="tool-add-image"]',
        '[data-tour="editor-properties"]',
      ],
      placement: 'canvas-center',
      eyebrow: 'Images',
      title: 'Add images your way',
      description: (
        <>
          <p>
            Upload your own images or choose from EDU Groove’s built-in
            Image Library.
          </p>

          <p>
            You can also copy one of our AI Image Style prompts into any
            AI image generator, then copy and paste the finished image
            straight back into EDU Groove.
          </p>
        </>
      ),
      onEnter: () => {
        setIsPropertiesCollapsed(false);
        handleToolClick('addImage');
      },
    },
    {
      selectors: [
        '[data-tour="tool-save"]',
        '[data-tour="editor-properties"]',
      ],
      placement: 'canvas-center',
      eyebrow: 'Save and Export',
      title: 'Save, continue or export',
      description: (
        <>
          <p>
            Download your editable project as a .groove file and upload
            it again whenever you want to continue working.
          </p>

          <p>
            You can also upload a pre-created PDF, export your finished
            work as a class-ready PDF, or use Advanced PDF Export to
            create wall art, posters and giant versions of your
            activities.
          </p>
        </>
      ),
      onEnter: () => {
        setIsPropertiesCollapsed(false);
        handleToolClick('save');
      },
    },
    {
      selector: '[data-tour="editor-top-menu"]',
      placement: 'bottom',
      eyebrow: 'Quick Actions',
      title: 'Use the top controls',
      description: (
        <p>
          These controls provide quick access to undo, redo, copy,
          paste, object order, locking and other common actions.
        </p>
      ),
      onEnter: () => {
        setIsPropertiesCollapsed(false);
        handleToolClick('select');
      },
    },
    {
      selector: '[data-tour="editor-pages"]',
      placement: 'top-wide-gap',
      eyebrow: 'Multiple Pages',
      title: 'Manage worksheet pages',
      description: (
        <p>
          Add pages, change the active page and drag pages into a
          different order using the controls along the bottom.
        </p>
      ),
      onEnter: () => {
        setIsPropertiesCollapsed(false);
        handleToolClick('select');
      },
    },
    {
      selector: '[data-tour="learn-tools-button"]',
      placement: 'bottom',
      highlightPadding: 6,
      eyebrow: 'Learn More',
      title: 'Learn each individual tool',
      description: (
        <>
          <p>
            You now know the main parts of EDU Groove and can start
            creating.
          </p>

          <p>
            When you are ready to learn more, click this question-mark
            button to open the full Learn the Tools walkthrough.
          </p>

          <p>
            You can return to it at any time.
          </p>
        </>
      ),
      nextLabel: 'Start Creating',
      onEnter: () => {
        setIsPropertiesCollapsed(false);
        handleToolClick('select');
      },
    },
  ];
  const [logoUrl, setLogoUrl] = useState(null);
  const [squareLogoUrl, setSquareLogoUrl] = useState(null); // Add this line
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [lastCopySource, setLastCopySource] = useState(null); // 'internal' or 'external'
  const [isAppReady, setIsAppReady] = useState(false);
  const [isEditorTourOpen, setIsEditorTourOpen] = useState(false);
  const [editorTourStep, setEditorTourStep] = useState(0);
  const [isToolsTourOpen, setIsToolsTourOpen] = useState(false);
  const [toolsTourStep, setToolsTourStep] = useState(0);
  const [hasCheckedEditorTour, setHasCheckedEditorTour] =
    useState(false);
  const [penMousePos, setPenMousePos] = useState(null);
    const setLogoPlaceholder = () => {
    const newId = Date.now().toString();

    const newShape = {
      id: newId,
      type: 'image',
      src: '__USER_LOGO__',
      x: 100,
      y: 100,
      width: 600,
      height: 150,
      opacity: 1,
    };

    setPages((prevPages) => {
      const updatedPages = prevPages.map((page, index) =>
        index === activePage
          ? { ...page, shapes: [...page.shapes, newShape] }
          : page
      );
      return updatedPages;
    });

    setSelectedShapeIds([newId]);
    setSelectedTool('select');
  };

    const setSquareLogoPlaceholder = () => {
      const newId = Date.now().toString();

      const newShape = {
        id: newId,
        type: 'image',
        src: '__USER_SQUARE_LOGO__',
        x: 100,
        y: 100,
        width: 200,
        height: 200,
        opacity: 1,
      };

      setPages((prevPages) => {
        const updatedPages = prevPages.map((page, index) =>
          index === activePage
            ? { ...page, shapes: [...page.shapes, newShape] }
            : page
        );
        return updatedPages;
      });

      setSelectedShapeIds([newId]);
      setSelectedTool('select');
    }; 
  

  const [showImageLibraryModal, setShowImageLibraryModal] = useState(false);
  const [isSwapImageMode, setIsSwapImageMode] = useState(false);
  const [swapTargetShapeId, setSwapTargetShapeId] = useState(null);
  const [imageLibraryInitialCategory, setImageLibraryInitialCategory] = useState(null);
  const [canvasBursts, setCanvasBursts] = useState([]);  
  
    const addImageToCanvas = (image) => {
      // ✅ If this is one of our user logo placeholders,
      // do NOT try to load it through new Image().
      // Just place it straight onto the canvas using fixed starter sizes.
      if (
        image.src === '__USER_LOGO__' ||
        image.src === '__USER_SQUARE_LOGO__'
      ) {
        const newShape = {
          id: Date.now().toString(),
          type: 'image',
          src: image.src,
          x: 100,
          y: 100,
          width: image.width || (image.src === '__USER_SQUARE_LOGO__' ? 200 : 600),
          height: image.height || (image.src === '__USER_SQUARE_LOGO__' ? 200 : 150),
          opacity: 1,
          meta: image.meta || undefined,
        };

        setPages((prevPages) => {
          const updatedPages = [...prevPages];
          updatedPages[activePage].shapes.push(newShape);
          return updatedPages;
        });

        setSelectedShapeIds([newShape.id]);
        setSelectedTool('select');        
        return;
      }

      const img = new window.Image();

      // Only use anonymous mode for data URLs or same-origin/public assets if needed later.
      // For now, let the browser load normally.
      img.src = image.src;

      img.onload = () => {
        const newShape = {
          id: Date.now().toString(),
          type: 'image',
          src: image.src,
          x: 100,
          y: 100,
          width: image.width || img.width,
          height: image.height || img.height,
          opacity: 1,
          meta: image.meta || undefined,
        };

        setPages((prevPages) => {
          const updatedPages = [...prevPages];
          updatedPages[activePage].shapes.push(newShape);
          return updatedPages;
        });

        setSelectedShapeIds([newShape.id]);
        setSelectedTool('select');        
      };

      img.onerror = () => {
        console.error('Error loading image:', img.src);
      };
    };


  const triggerCanvasBurst = (shape) => {
    if (!shape) return;

    const width = (shape.width || 0) * Math.abs(shape.scaleX || 1);
    const height = (shape.height || 0) * Math.abs(shape.scaleY || 1);

    const burst = {
      id: `burst-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      shapeId: shape.id.toString(),
      x: shape.x || 0,
      y: shape.y || 0,
      radius: Math.max(24, Math.min(Math.max(width, height) * 0.28, 60)),
      createdAt: Date.now(),
    };

    setCanvasBursts((prev) => [...prev, burst]);
  };

  const handleMovePage = (fromIndex, toIndex) => {
    if (fromIndex === toIndex) return;

    setPages((prevPages) => {
      const updatedPages = [...prevPages];
      const [movedPage] = updatedPages.splice(fromIndex, 1);
      updatedPages.splice(toIndex, 0, movedPage);
      return updatedPages;
    });

    setActivePage((prevActivePage) => {
      if (prevActivePage === fromIndex) return toIndex;

      if (fromIndex < prevActivePage && toIndex >= prevActivePage) {
        return prevActivePage - 1;
      }

      if (fromIndex > prevActivePage && toIndex <= prevActivePage) {
        return prevActivePage + 1;
      }

      return prevActivePage;
    });
  };

  const handleImageSelect = (image) => {
    // ✅ Swap mode: replace existing image instead of adding a new one
    if (isSwapImageMode && swapTargetShapeId) {
      const targetShape = pages[activePage].shapes.find(
        (shape) => shape.id.toString() === swapTargetShapeId.toString()
      );

      if (!targetShape) {
        console.warn('Swap target shape not found.');
        setIsSwapImageMode(false);
        setSwapTargetShapeId(null);
        setImageLibraryInitialCategory(null);
        addImageToCanvas(image);
        return;
      }

      // Handle logo placeholders immediately
      if (
        image.src === '__USER_LOGO__' ||
        image.src === '__USER_SQUARE_LOGO__'
      ) {
        pushToUndoStack();

        const updatedShape = {
          ...targetShape,
          src: image.src,
          width: targetShape.width,
          height: targetShape.height,
          x: targetShape.x,
          y: targetShape.y,
          rotation: targetShape.rotation || 0,
          scaleX: targetShape.scaleX || 1,
          scaleY: targetShape.scaleY || 1,
          opacity: targetShape.opacity ?? 1,
          locked: targetShape.locked || false,
          groupId: targetShape.groupId,
          meta: image.meta || undefined,
        };

        const updatedPages = [...pages];
        updatedPages[activePage].shapes = updatedPages[activePage].shapes.map((shape) => {
          if (shape.id.toString() !== swapTargetShapeId.toString()) return shape;
          return updatedShape;
        });

        setPages(updatedPages);
        setSelectedShapeIds([swapTargetShapeId.toString()]);
        setSelectedTool('select');
        setShowImageLibraryModal(false);
        setIsSwapImageMode(false);
        setSwapTargetShapeId(null);
        setImageLibraryInitialCategory(null);

        triggerCanvasBurst(updatedShape);
        return;
      }

      const img = new window.Image();
      img.src = image.src;

      img.onload = () => {
        pushToUndoStack();

        const updatedShape = {
          ...targetShape,
          src: image.src,
          width: targetShape.width,
          height: targetShape.height,
          x: targetShape.x,
          y: targetShape.y,
          rotation: targetShape.rotation || 0,
          scaleX: targetShape.scaleX || 1,
          scaleY: targetShape.scaleY || 1,
          opacity: targetShape.opacity ?? 1,
          locked: targetShape.locked || false,
          groupId: targetShape.groupId,
          meta: image.meta || undefined,
        };

        const updatedPages = [...pages];
        updatedPages[activePage].shapes = updatedPages[activePage].shapes.map((shape) => {
          if (shape.id.toString() !== swapTargetShapeId.toString()) return shape;
          return updatedShape;
        });

        setPages(updatedPages);
        setSelectedShapeIds([swapTargetShapeId.toString()]);
        setSelectedTool('select');
        setShowImageLibraryModal(false);
        setIsSwapImageMode(false);
        setSwapTargetShapeId(null);
        setImageLibraryInitialCategory(null);

        triggerCanvasBurst(updatedShape);
      };

      img.onerror = () => {
        console.error('Error loading swap image:', image.src);
      };

      return;
    }

    // ✅ Normal add mode
    addImageToCanvas(image);
  };

  const openSwapImageModal = async (shapeId) => {
    const targetShape = pages[activePage]?.shapes?.find(
      (shape) => shape.id.toString() === shapeId.toString()
    );

    if (!targetShape) return;

    let savedCategory = targetShape?.meta?.libraryCategory || 'All';

    // Backward compatibility for older .groove files
    if (savedCategory === 'All' && typeof targetShape?.src === 'string') {
      const match = targetShape.src.match(/\/(?:app\/)?Icons\/([^/]+)\//i);
      if (match?.[1]) {
        savedCategory = decodeURIComponent(match[1]);
      }
    }

    // If this is an older SVG image, fetch and save svgOriginal before swap
    const isLegacyEduGrooveSvg =
      typeof targetShape?.src === 'string' &&
      (
        targetShape.src.includes('/Icons/') ||
        targetShape.src.includes('/app/Icons/')
      ) &&
      targetShape.src.toLowerCase().endsWith('.svg') &&
      !targetShape?.meta?.svgOriginal;

    if (isLegacyEduGrooveSvg) {
      try {
        let fetchUrl = targetShape.src;

        // convert old saved path like /app/Icons/... to public path /Icons/...
        fetchUrl = fetchUrl.replace(/^\/app\/Icons\//i, '/Icons/');

        const response = await fetch(fetchUrl);
        const svgText = await response.text();

        const updatedPages = [...pages];
        updatedPages[activePage].shapes = updatedPages[activePage].shapes.map((shape) => {
          if (shape.id.toString() !== shapeId.toString()) return shape;

          return {
            ...shape,
            meta: {
              ...(shape.meta || {}),
              svgOriginal: svgText,
              preserveWhiteFill: shape?.meta?.preserveWhiteFill ?? true,
              fillColor: shape?.meta?.fillColor ?? '#ffffff',
              strokeColor: shape?.meta?.strokeColor ?? '#000000',
              isSvg: true,
              libraryCategory: savedCategory,
            },
          };
        });

        setPages(updatedPages);
      } catch (error) {
        console.error('Failed to hydrate legacy SVG for swap:', error);
      }
    }

    setSwapTargetShapeId(shapeId);
    setIsSwapImageMode(true);
    setImageLibraryInitialCategory(savedCategory);
    setShowImageLibraryModal(true);
  };


  // Define the copyToAllPages function
// Define the copyToAllPages function
  const copyToAllPages = () => {
    if (selectedShapeIds.length === 0) return;

    // Identify which selected shapes are locked and which are not
    const lockedShapes = pages[activePage].shapes.filter(
      (shape) =>
        selectedShapeIds.includes(shape.id.toString()) && shape.locked
    );
    const unlockedShapes = pages[activePage].shapes.filter(
      (shape) =>
        selectedShapeIds.includes(shape.id.toString()) && !shape.locked
    );

    // If all selected shapes are locked, notify the user and prevent copying
    if (lockedShapes.length === selectedShapeIds.length) {
      alert('Cannot copy selected shapes because they are locked.');
      return;
    }

    // If some selected shapes are locked, notify the user that only unlocked shapes will be copied
    if (lockedShapes.length > 0) {
      alert('Some selected shapes are locked and could not be copied.');
    }

    pushToUndoStack(); // Save current state for undo functionality

    const updatedPages = pages.map((page, index) => {
      if (index === activePage) {
        // Skip the active page
        return page;
      }

      // ✅ Use one groupId map PER DESTINATION PAGE so grouped shapes
      // on that page keep the same (new) groupId among themselves
      const groupIdMap = {};

      // ✅ Clone each unlocked shape with brand-new ids everywhere needed
      const copiedShapes = unlockedShapes.map((shape) => {
        const clone = cloneShapeWithNewIds(shape, groupIdMap);
        return clone;
      });

      // Insert copied shapes at the beginning to place them behind other objects (or change order if you prefer)
      return {
        ...page,
        shapes: [...copiedShapes, ...page.shapes],
      };
    });

    setPages(updatedPages);
    setSelectedShapeIds([]); // Optionally, clear the selection after copying
  };



  const [exportFilename, setExportFilename] = useState('');

  const [workspaceBackgroundImage, setWorkspaceBackgroundImage] = useState(craftyImage);
  useEffect(() => {
    
    const savedMood = localStorage.getItem('workspaceMood') || 'EDUGroove';
    if (savedMood === 'Clean') {
      setWorkspaceBackgroundImage(null);
      setWorkspaceBackgroundColor('#FFF6E6');
    } else if (savedMood && backgroundImages[savedMood]) {
      setWorkspaceBackgroundImage(backgroundImages[savedMood]);
      setWorkspaceBackgroundColor(null);
    }
  }, []);  

  useEffect(() => {
    if (!isAppReady) return;
    if (hasCheckedEditorTour) return;

    setHasCheckedEditorTour(true);

    const shouldContinueTour =
      sessionStorage.getItem(
        'edugroove_continue_walkthrough'
      ) === 'true';

    if (!shouldContinueTour) return;

    sessionStorage.removeItem(
      'edugroove_continue_walkthrough'
    );

    window.setTimeout(() => {
      setEditorTourStep(0);
      setIsEditorTourOpen(true);
    }, 450);
  }, [isAppReady, hasCheckedEditorTour]);

  const closeEditorTour = () => {
    localStorage.setItem(
      tourCompletedStorageKey,
      'true'
    );

    sessionStorage.removeItem(
      'edugroove_continue_walkthrough'
    );

    setIsEditorTourOpen(false);
    setEditorTourStep(0);
  };

  const finishEditorTour = () => {
    localStorage.setItem(
      tourCompletedStorageKey,
      'true'
    );

    sessionStorage.removeItem(
      'edugroove_continue_walkthrough'
    );

    setIsEditorTourOpen(false);
    setEditorTourStep(0);
  };
  
  const backgroundImages = {
    Calm: calmImage,
    Energetic: energeticImage,
    Creative: creativeImage,
    Crafty: craftyImage, 
    Workbench: workbenchImage,
    Cute: cuteImage,
    Hungry: hungryImage,
    Zen: zenImage,
    EDUGroove: eduGrooveImage,
  };

  // ✅ Preload background images on initial render
  useEffect(() => {
    Object.values(backgroundImages).forEach((img) => {
      const preloadImg = new Image();
      preloadImg.src = img;
    });
  }, []);

  useEffect(() => {
    const loadFonts = async () => {
      await preloadAppFonts();
    };
  
    const preloadBackgrounds = async () => {
      const backgroundPromises = Object.values(backgroundImages).map((img) => {
        return new Promise((resolve) => {
          const preloadImg = new Image();

          preloadImg.onload = async () => {
            try {
              if (preloadImg.decode) {
                await preloadImg.decode();
              }
            } catch (err) {
              console.warn('Background decode skipped:', img);
            }

            resolve();
          };

          preloadImg.onerror = resolve;
          preloadImg.src = img;
        });
      });

      await Promise.all(backgroundPromises);
    };
  
    const initializeApp = async () => {
      setLoaderMessage("Preparing Workspace");
      setIsLoading(true);
  
      await Promise.all([
        loadFonts(),
        preloadBackgrounds(),
        new Promise(resolve => setTimeout(resolve, 500)) // Small safety delay
      ]);
  
      setIsAppReady(true); // App is now fully ready
      setIsLoading(false);
      setLoaderMessage('');
    };
  
    initializeApp();
  }, []);
  
    useEffect(() => {
      const historyState = {
        eduGrooveBackGuard: true,
        time: Date.now(),
      };

      // Add a guard entry so browser Back / trackpad swipe hits this first
      window.history.pushState(historyState, '', window.location.href);

      const handlePopState = () => {
        const shouldLeave = window.confirm(
          'You have unsaved work. Are you sure you want to leave this page?'
        );

        if (shouldLeave) {
          window.removeEventListener('popstate', handlePopState);
          window.history.back();
        } else {
          // Re-add the guard so they stay on the page
          window.history.pushState(historyState, '', window.location.href);
        }
      };

      window.addEventListener('popstate', handlePopState);

      return () => {
        window.removeEventListener('popstate', handlePopState);
      };
    }, []);

  // Define the toggleEditMode function
  const toggleEditMode = () => {
    setIsEditMode((prevMode) => !prevMode);
  };

  const [selectedTool, setSelectedTool] = useState('select'); // Initialize as 'select'
  const [bucketSettings, setBucketSettings] = useState({
    color: '#ff0000',
    target: 'auto', // 'auto' | 'fill' | 'stroke' | 'text'
  });
  const [hoveredTool, setHoveredTool] = useState(null);
  const [isPropertiesCollapsed, setIsPropertiesCollapsed] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(true);
  const [scale, setScale] = useState(1);
  const [penPoints, setPenPoints] = useState([]);
  const [penTempLineId, setPenTempLineId] = useState(null);
  
  
  const [activePage, setActivePage] = useState(() => {
    if (initialState && initialState.activePage !== undefined) {
      return initialState.activePage;
    } else {
      return 0;
    }
  });  


  // ✅ Ensure state is initialized before useEffect runs
  const [pages, setPages] = useState(() => {
    const storedOrientation = localStorage.getItem('canvasOrientation') || 'landscape';
    const isPortrait = storedOrientation === 'portrait';

    return [
      {
        id: 0,
        shapes: [],
        backgroundColor: '#ffffff',
        orientation: storedOrientation,
        width: isPortrait ? 794 : 1123,
        height: isPortrait ? 1123 : 794,
      },
    ];
  });


// ✅ Now `pages` is initialized before useEffect runs
useEffect(() => {
  const fetchLogo = async () => {
    let userLogo = null;
    let userSquareLogo = null;

    if (currentUser?.id) {
      try {
        console.log('Fetching logos for user:', currentUser.id);

        const res = await axios.get(`https://edugroove-backend.onrender.com/api/users/${currentUser.id}/logos`);

        console.log('Logo response:', res.data);

        if (res.data.logoUrl) {
          userLogo = res.data.logoUrl;
        }
        if (res.data.squareLogoUrl) {
          userSquareLogo = res.data.squareLogoUrl;
        }
      } catch (err) {
        console.warn('No user-specific logo found.');
      }
    }

    if (!userLogo) {
      try {
        const defaultRes = await axios.get('https://edugroove-backend.onrender.com/api/admin/getDefaultLogo');
        userLogo = defaultRes.data.defaultLogoUrl;
      } catch (err) {
        console.error('Error fetching default rectangular logo:', err);
      }
    }

    if (!userSquareLogo) {
      try {
        const squareDefaultRes = await axios.get('https://edugroove-backend.onrender.com/api/admin/getDefaultLogo?type=square');
        userSquareLogo = squareDefaultRes.data.defaultLogoUrl;
      } catch (err) {
        console.error('Error fetching default square logo:', err);
      }
    }

    setLogoUrl(userLogo);
    setSquareLogoUrl(userSquareLogo);
  };

  fetchLogo();
}, [currentUser]);

 // ✅ Runs when the user logs in, even as a guest





useEffect(() => {
  const handleApplyMask = () => applyPenMask();
  const handleReleaseMask = () => releasePenMask();

  window.addEventListener('applyPenMask', handleApplyMask);
  window.addEventListener('releasePenMask', handleReleaseMask);

  return () => {
    window.removeEventListener('applyPenMask', handleApplyMask);
    window.removeEventListener('releasePenMask', handleReleaseMask);
  };
}, []);


  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showLastPageModal, setShowLastPageModal] = useState(false);
  const [deletePageId, setDeletePageId] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [shapeProperties, setShapeProperties] = useState({
    strokeWidth: 2,
    strokeColor: '#000000',
    fillColor: '#FFFFFF',
    fillStyle: 'solid',
    fillSecondaryColor: '#F2D6A7',
    fillPatternSize: 10,
    fillPatternSpacing: 8,
    fillPatternRotation: 0,
    opacity: 1,
    cornerRadius: 10,
    dashEnabled: false,
    dash: [10, 5],
    strokeStyle: 'solid',
    strokeWaveSize: 8,
    strokeWaveFrequency: 6,
    strokeEnabled: true,
    fillEnabled: true,
    gridRows: 3,
    gridColumns: 3,
    segments: 4,
    arcAngle: 180,
    numPoints: 5,
    trianglePeakOffset: 0,
    mazeRows: 8,
    mazeColumns: 8,
    mazeExits: 2,
    mazeComplexity: 3,
    wordSearchRows: 5,
    wordSearchColumns: 5,
    wordSearchFontFamily: 'Arial',
    wordSearchFontSize: 24,
    wordSearchPressEnterToAdvance: false,
    wordSearchPaintTextEnabled: false,
    wordSearchPaintTextValue: '',
    wordSearchSelectedCellIndex: null,
    wordSearchLetter: '',
  });
  const [lineProperties, setLineProperties] = useState({
    strokeWidth: 2,
    strokeColor: '#000000',
    opacity: 1,
    dashEnabled: false,
    dash: [10, 5],
    tracingPoints: 5,
    tracingLength: 300,
  });
  const [penToolSettings, setPenToolSettings] = useState({
    strokeColor: '#000000',
    strokeWidth: 2,
    fillColor: '#ffffff',
    fillEnabled: true,
    strokeEnabled: true,
    dashEnabled: false,
    opacity: 1,
    tension: 0.5,
    bezierEnabled: false,
  });
  const [pencilProperties, setPencilProperties] = useState({
    strokeWidth: 2,
    strokeColor: '#000000',
    tension: 1,
    dashEnabled: false, // Add this line
    dash: [10, 5],       // Add this line (default dash pattern)
    opacity: 1,
  });
  const [textProperties, setTextProperties] = useState({
    text: '', // Add this line
    fontFamily: 'Arial',
    fontSize: 20,
    fill: '#000000',
    opacity: 1,
    bold: false,
    italic: false,
    underline: false,
    align: 'left',
    width: 200,
    height: 100,
  });
  const [penProperties, setPenProperties] = useState({
    strokeColor: '#000000',
    strokeWidth: 2,
    opacity: 1,
  });
  
  const [selectedShapeType, setSelectedShapeType] = useState('rect');
  const [drawing, setDrawing] = useState(false);
  const [selectedShapeIds, setSelectedShapeIds] = useState([]);
  const transformerRef = useRef(null);
  const imageInputRef = useRef(null);
  const [offset, setOffset] = useState({
    startX: 0,
    startY: 0,
    initialX: 0,
    initialY: 0,
  });
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const [workspaceBackgroundColor, setWorkspaceBackgroundColor] =
    useState('#f0f0f0');
  const [selectionRect, setSelectionRect] = useState(null);
  const selectionStartPoint = useRef(null);
  const selectionIgnoreShapeIdRef = useRef(null);
  const [isSelecting, setIsSelecting] = useState(false);
  const stageRef = useRef(null);
  const isMiddleMouseDownRef = useRef(false);
  const middleMouseStartRef = useRef({ x: 0, y: 0, panX: 0, panY: 0 });
  const [isMiddleGrabbing, setIsMiddleGrabbing] = useState(false);
  const initialPositionsRef = useRef({});
  const contextMenuRef = useRef(null);
  const initialPointerPosRef = useRef({ x: 0, y: 0 });
  const [isEditingText, setIsEditingText] = useState(false);
  const [editingTextPosition, setEditingTextPosition] = useState({
    x: 0,
    y: 0,
    rotation: 0,
    scaleX: 1,
    scaleY: 1,
    fontSize: 20,
    fontFamily: 'Arial',
    fill: '#000000',
    bold: false,
    italic: false,
    underline: false,
    opacity: 1,
    align: 'left',
  });
  const [editingTextId, setEditingTextId] = useState(null);
  const [editingTextValue, setEditingTextValue] = useState('');
  const undoStack = useRef([]);
  const redoStack = useRef([]);
  const propertyHistoryKeyRef = useRef(null);
  const propertyHistoryTimerRef = useRef(null);
  const copiedShapesRef = useRef([]);
  const lastExternalImageRef = useRef(null); // ✅ Cache the last pasted external image data
  const [canPaste, setCanPaste] = useState(false);
  const [isShiftPressed, setIsShiftPressed] = useState(false);
  const [isGridVisible, setIsGridVisible] = useState(false);
  const [isRulerGridVisible, setIsRulerGridVisible] = useState(false);
  const [isMeasurementRulerVisible, setIsMeasurementRulerVisible] =
  useState(false);
  const [isMeasurementRulerSelected, setIsMeasurementRulerSelected] =
  useState(false);

  const [measurementRulerSettings, setMeasurementRulerSettings] = useState({
    unit: 'cm',
    x: 397,
    y: 120,
    rotation: 0,
  });
  const [isQuartersGridVisible, setIsQuartersGridVisible] = useState(false);
  const [isThirdsGridVisible, setIsThirdsGridVisible] = useState(false);
  const [isCustomGridVisible, setIsCustomGridVisible] = useState(false);
  const [customGridSettings, setCustomGridSettings] = useState({
    horizontalSections: 4,
    verticalSections: 4,
  });
  const [isEditMode, setIsEditMode] = useState(false); // Initialize as 
  const [isLoading, setIsLoading] = useState(false); // New state to manage loading spinner
  const [loaderMessage, setLoaderMessage] = useState('');
  const [editingTemplate, setEditingTemplate] = useState(null);

  useEffect(() => {
    const initialState = location.state?.initialState;
    const incomingTemplate = location.state?.editingTemplate;

    if (initialState) {
      setPages(initialState.pages || []);
      setActivePage(0);
    }

    if (incomingTemplate) {
      setEditingTemplate(incomingTemplate);

      // Automatically use the template name as the export filename.
      if (incomingTemplate.name) {
        setExportFilename(incomingTemplate.name);
      }

      // Prevent the template from loading again after navigation changes.
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);


  const isMovingRef = useRef(false);

  // Custom Context Menu State
  const [contextMenuVisible, setContextMenuVisible] = useState(false);
  const [contextMenuPosition, setContextMenuPosition] = useState({ x: 0, y: 0 });
  

  // Add and duplicate page functions
  const addBlankPage = () => {
    pushToUndoStack();
    const newPage = {
      id: pages.length,
      shapes: [],
      backgroundColor: '#ffffff',
      orientation: pages[activePage].orientation,
      width: pages[activePage].orientation === 'portrait' ? 794 : 1123,
      height: pages[activePage].orientation === 'portrait' ? 1123 : 794,
    };
    const updatedPages = [...pages, newPage];
    setPages(updatedPages);
    setActivePage(updatedPages.length - 1);
  };

  // === ID + Clone Utilities (NEW) ===

// Small unique id helper (Date + random string)
  const makeId = () => `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

    /**
     * Deep-clone a shape and assign fresh ids everywhere needed.
     * - Handles normal shapes
     * - Handles maskedGroup (also refreshes child ids and keeps relative offsets)
     * - Handles groupId remapping so grouped shapes stay grouped but not colliding with other pages
     *
     * @param {Object} shape - the source shape
     * @param {Object} groupIdMap - map of oldGroupId -> newGroupId for this clone batch
     * @returns {Object} cloned shape with new ids
     */
    const cloneShapeWithNewIds = (shape, groupIdMap = {}) => {
      const copy = JSON.parse(JSON.stringify(shape));

      // Give the top-level shape a fresh id
      copy.id = makeId();

      // Remap groupId consistently for the batch
      if (copy.groupId) {
        if (!groupIdMap[copy.groupId]) {
          groupIdMap[copy.groupId] = makeId();
        }
        copy.groupId = groupIdMap[copy.groupId];
      }

      // Special handling for maskedGroup
      if (copy.type === 'maskedGroup' && Array.isArray(copy.children)) {
        // Make sure the top-level x/y exists (fallback to content shape)
        const content = copy.children.find(c => c && c.type && c.type !== 'rect' && c.type !== 'pen') || copy.children[0];
        const groupX = typeof copy.x === 'number' ? copy.x : (content?.x || 0);
        const groupY = typeof copy.y === 'number' ? copy.y : (content?.y || 0);

        copy.children = copy.children.map(child => {
          const childClone = { ...child };
          childClone.id = makeId();

          // Preserve relative offset to group
          const baseX = typeof childClone.x === 'number' ? childClone.x : 0;
          const baseY = typeof childClone.y === 'number' ? childClone.y : 0;

          // If your logic expects children to include the group position baked in, keep as-is.
          // If you store top-level x/y on the group, you can just return childClone unchanged.
          // We’ll keep children positions as they were, and rely on group x/y on the parent.
          childClone.x = baseX;
          childClone.y = baseY;

          // If children have groupId as well, remap them too
          if (childClone.groupId) {
            if (!groupIdMap[childClone.groupId]) {
              groupIdMap[childClone.groupId] = makeId();
            }
            childClone.groupId = groupIdMap[childClone.groupId];
          }

          return childClone;
        });

        // Ensure the group itself has explicit x/y for consistent transforms
        copy.x = groupX;
        copy.y = groupY;
      }

      return copy;
    };


  const duplicateCurrentPage = () => {
    pushToUndoStack();

    const currentPage = pages[activePage];

    // Deep copy the page
    const pageCopy = JSON.parse(JSON.stringify(currentPage));

    // Regenerate every shape id (and groupId) on the new page
    const groupIdMap = {};
    const remappedShapes = pageCopy.shapes.map(s => cloneShapeWithNewIds(s, groupIdMap));

    const newPage = {
      ...pageCopy,
      id: pages.length,       // new page id
      shapes: remappedShapes, // fresh ids inside
    };

    const updatedPages = [...pages, newPage];
    setPages(updatedPages);

    // Move to the new page and clear any selection to avoid stale ids
    setActivePage(updatedPages.length - 1);
    setSelectedShapeIds([]);
  };


  // Define the addText function
  const addText = (x, y) => {
    const newText = {
      id: Date.now(),
      type: 'text',
      x: x,
      y: y,
      rotation: 0,
      scaleX: 1,
      scaleY: 1,
      text: 'Your Text Here',
      align: 'left', // default alignment
      width: 200,    // set a default width
      height: 100,   // set a default height
      fontSize: textProperties.fontSize,
      fontFamily: textProperties.fontFamily,
      fill: textProperties.fill,
      opacity: textProperties.opacity,
      bold: textProperties.bold,
      italic: textProperties.italic,
      underline: textProperties.underline,
      locked: false, // Initialize as unlocked
    };

    const updatedPages = [...pages];
    updatedPages[activePage].shapes.push(newText);
    setPages(updatedPages);
    setSelectedShapeIds([newText.id.toString()]);
    setIsEditingText(true);
    setEditingTextPosition({ x, y });
    setEditingTextId(newText.id.toString());
    setEditingTextValue(newText.text);
    setSelectedTool('select'); // Switch to the selection tool
  };

  const fileInputRef = useRef(null);
    const grooveUploadModeRef = useRef('replace');
    const pdfInputRef = useRef(null);

    const triggerFileInput = (mode = 'replace') => {
      grooveUploadModeRef.current = mode;
      if (fileInputRef.current) {
        fileInputRef.current.click();
      }
    };

    const triggerPDFInput = () => {
      if (pdfInputRef.current) {
        pdfInputRef.current.click();
      }
    };

  const loadStateFromJSON = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setIsLoading(true);
  
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const loadedState = JSON.parse(event.target.result);

        if (!Array.isArray(loadedState.pages) || loadedState.pages.length === 0) {
          throw new Error('The .groove file does not contain any pages.');
        }

        if (grooveUploadModeRef.current === 'append') {
          pushToUndoStack();

          const firstImportedPageIndex = pages.length;
          const combinedPages = [...pages, ...loadedState.pages].map((page, index) => ({
            ...page,
            id: index,
          }));

          setPages(combinedPages);
          setActivePage(firstImportedPageIndex);
          setSelectedShapeIds([]);
          setSelectedTool('select');
          alert(`${loadedState.pages.length} page(s) added to the end of your current work.`);
        } else {
          setPages(
            loadedState.pages.map((page, index) => ({
              ...page,
              id: index,
            }))
          );
          setActivePage(
            Number.isInteger(loadedState.activePage) &&
              loadedState.activePage >= 0 &&
              loadedState.activePage < loadedState.pages.length
              ? loadedState.activePage
              : 0
          );
          setSelectedShapeIds([]);
          setSelectedTool('select');
          alert('Your work has been loaded.');
        }

        setIsLoading(false);
      } catch (error) {
        console.error('Failed to load .groove file:', error);
        setIsLoading(false);
        alert('Failed to load file. Please ensure it is a valid .groove file.');
      } finally {
        e.target.value = '';
      }
    };
    reader.onerror = () => {
      setIsLoading(false);
      e.target.value = '';
      alert('Failed to read the selected .groove file.');
    };
    reader.readAsText(file);
  };  

  const handlePDFUpload = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;

    try {
      pushToUndoStack();
      setLoaderMessage('Loading PDF...');
      setIsLoading(true);
      setSelectedShapeIds([]);

      const arrayBuffer = await file.arrayBuffer();
      const pdfjsLib = getPdfJsLib();
        const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;

      const importedPages = [];

      for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
        setLoaderMessage(`Importing PDF page ${pageNum} of ${pdf.numPages}...`);

        const pdfPage = await pdf.getPage(pageNum);

        const baseViewport = pdfPage.getViewport({ scale: 1 });
        const orientation =
          baseViewport.width > baseViewport.height ? 'landscape' : 'portrait';

        const pageWidth = orientation === 'portrait' ? 794 : 1123;
        const pageHeight = orientation === 'portrait' ? 1123 : 794;

        // Render at a higher quality, but display fitted exactly to the app page
        const renderScale = 2;
        const viewport = pdfPage.getViewport({ scale: renderScale });

        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        if (!context) {
          throw new Error('Could not create canvas context for PDF import.');
        }

        canvas.width = Math.ceil(viewport.width);
        canvas.height = Math.ceil(viewport.height);

        context.fillStyle = '#ffffff';
        context.fillRect(0, 0, canvas.width, canvas.height);

        await pdfPage.render({
          canvasContext: context,
          viewport,
        }).promise;

        const dataUrl = canvas.toDataURL('image/png');

        const pdfImageShape = {
          id: makeId(),
          type: 'image',
          src: dataUrl,
          x: pageWidth / 2,
          y: pageHeight / 2,
          width: pageWidth,
          height: pageHeight,
          rotation: 0,
          scaleX: 1,
          scaleY: 1,
          locked: true,
          meta: {
            isPdfImport: true,
            originalPageNumber: pageNum,
            sourceFileName: file.name,
          },
        };

        importedPages.push({
          id: pageNum - 1,
          shapes: [pdfImageShape],
          backgroundColor: '#ffffff',
          orientation,
          width: pageWidth,
          height: pageHeight,
        });
      }

      if (importedPages.length > 0) {
        setPages(importedPages);
        setActivePage(0);
        setSelectedTool('select');
        alert(`PDF loaded successfully. Imported ${importedPages.length} page(s).`);
      }
    } catch (error) {
      console.error('PDF import failed:', error);
      alert('Failed to load PDF. Please try another PDF file.');
    } finally {
      setIsLoading(false);
      setLoaderMessage('');
      e.target.value = '';
    }
  };
  
  const saveStateAsJSON = () => {
    setLoaderMessage("Preparing .groove file");
    setIsLoading(true);
  
    const stateToSave = {
      fileType: 'EDUGROOVE',
      version: '1.0',
      pages,
      activePage,
    };
  
    const prettyJson = JSON.stringify(stateToSave, null, 2);
    const blob = new Blob([prettyJson], { type: 'application/json' });
  
    const exportFileDefaultName = sanitizeFilename(exportFilename, 'groove');
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = exportFileDefaultName;
    link.click();
  
    // Delay hiding for a smoother UI
    setTimeout(() => {
      setIsLoading(false);
      setLoaderMessage('');
    }, 800);
  };
  
  

  const [isExporting, setIsExporting] = useState(false); // New state variable  

  const [showAdvancedPDFExportModal, setShowAdvancedPDFExportModal] = useState(false);

  // Inside MainApp.js or wherever your export logic lives
  const exportToPDF = async () => {
    setIsExporting(true);
    setLoaderMessage('Exporting...');
    setIsLoading(true);

    const originalPage = activePage;

    const originalGridState = {
      isGridVisible,
      isRulerGridVisible,
      isQuartersGridVisible,
      isThirdsGridVisible,
      isCustomGridVisible,
    };

    setIsGridVisible(false);
    setIsRulerGridVisible(false);
    setIsQuartersGridVisible(false);
    setIsThirdsGridVisible(false);
    setIsCustomGridVisible(false);
    await new Promise((resolve) => setTimeout(resolve, 100));

    const pdf = new jsPDF(
      pages[0]?.orientation === 'portrait' ? 'p' : 'l',
      'pt',
      'a4'
    );

    try {
      for (let i = 0; i < pages.length; i++) {
        const page = pages[i];
        const isFirst = i === 0;

        setActivePage(i);
        await new Promise((resolve) => setTimeout(resolve, 100));

        const stage = stageRef.current;
        if (!stage) throw new Error('Stage not ready');

        if (transformerRef.current) transformerRef.current.nodes([]);
        stage.batchDraw();

        const dataURL = stage.toDataURL({ pixelRatio: 2 });

        const image = new Image();
        image.src = dataURL;
        await new Promise((resolve) => (image.onload = resolve));

        const canvas = document.createElement('canvas');
        canvas.width = image.width;
        canvas.height = image.height;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#fff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(image, 0, 0);

        const flattened = canvas.toDataURL('image/png');
        const imgProps = pdf.getImageProperties(flattened);
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

        if (!isFirst) {
          pdf.addPage('a4', page.orientation === 'portrait' ? 'p' : 'l');
        }

        pdf.addImage(flattened, 'PNG', 0, 0, pdfWidth, pdfHeight);
      }

      const filename = sanitizeFilename(exportFilename || 'canvas', 'pdf');
      pdf.save(filename);
      alert('PDF exported!');
    } catch (err) {
      console.error('Export failed:', err);
      alert('Something went wrong.');
    } finally {
      setIsExporting(false);
      setIsGridVisible(originalGridState.isGridVisible);
      setIsRulerGridVisible(originalGridState.isRulerGridVisible);
      setIsQuartersGridVisible(originalGridState.isQuartersGridVisible);
      setIsThirdsGridVisible(originalGridState.isThirdsGridVisible);
      setIsCustomGridVisible(originalGridState.isCustomGridVisible);
      setActivePage(originalPage);
      setIsLoading(false);
      setLoaderMessage('');
    }
  };

  const loadImageFromDataURL = (dataURL) => {
  return new Promise((resolve, reject) => {
    const image = new window.Image();
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = dataURL;
  });
};

  const exportAdvancedPDF = async (mode) => {
    let columns = mode?.columns || 2;
    let rows = mode?.rows || 2;

    // Printer Safe Mode
    const printerSafe = mode?.printerSafe || false;
    const MM_TO_PT = 72 / 25.4;
    const printerSafeInset = 5 * MM_TO_PT;

    setShowAdvancedPDFExportModal(false);
    setIsExporting(true);
    setLoaderMessage(`Exporting ${mode?.label || 'Advanced PDF'}...`);
    setIsLoading(true);

    const originalPage = activePage;

    const originalGridState = {
      isGridVisible,
      isRulerGridVisible,
      isQuartersGridVisible,
      isThirdsGridVisible,
      isCustomGridVisible,
    };

    setIsGridVisible(false);
    setIsRulerGridVisible(false);
    setIsQuartersGridVisible(false);
    setIsThirdsGridVisible(false);
    setIsCustomGridVisible(false);

    await new Promise((resolve) => setTimeout(resolve, 100));

    const firstOrientation =
    mode?.key === 'mini'
      ? pages[0]?.orientation === 'portrait'
        ? 'l'
        : 'p'
      : pages[0]?.orientation === 'portrait'
        ? 'p'
        : 'l';

  const pdf = new jsPDF(firstOrientation, 'pt', 'a4');

    let hasAddedFirstPage = false;

    try {
      for (let pageIndex = 0; pageIndex < pages.length; pageIndex++) {
        const page = pages[pageIndex];

        setLoaderMessage(
          `Exporting page ${pageIndex + 1} of ${pages.length}...`
        );

        setActivePage(pageIndex);
        await new Promise((resolve) => setTimeout(resolve, 150));

        const stage = stageRef.current;
        if (!stage) throw new Error('Stage not ready');

        if (transformerRef.current) transformerRef.current.nodes([]);
        stage.batchDraw();

        const dataURL = stage.toDataURL({ pixelRatio: 3 });
        const sourceImage = await loadImageFromDataURL(dataURL);

        const flattenedCanvas = document.createElement('canvas');
        flattenedCanvas.width = sourceImage.width;
        flattenedCanvas.height = sourceImage.height;

        const flattenedCtx = flattenedCanvas.getContext('2d');
        flattenedCtx.fillStyle = '#ffffff';
        flattenedCtx.fillRect(0, 0, flattenedCanvas.width, flattenedCanvas.height);
        flattenedCtx.drawImage(sourceImage, 0, 0);

        const flattenedDataURL = flattenedCanvas.toDataURL('image/png');
        const flattenedImage = await loadImageFromDataURL(flattenedDataURL);

        const orientation = page.orientation === 'portrait' ? 'p' : 'l';

        
        // ✅ MINI MODE
        // Portrait worksheets export to landscape A4 with 2 copies side by side.
        // Landscape worksheets export to portrait A4 with 2 copies stacked.
        // Printer safe is applied inside each mini slot, so the centre edges also get spacing.
        if (mode?.key === 'mini') {
          const isPortraitMini = page.orientation === 'portrait';
          const miniPdfOrientation = isPortraitMini ? 'l' : 'p';

          if (hasAddedFirstPage) {
            pdf.addPage('a4', miniPdfOrientation);
          } else {
            hasAddedFirstPage = true;
          }

          const pdfWidth = pdf.internal.pageSize.getWidth();
          const pdfHeight = pdf.internal.pageSize.getHeight();

          // Use the full PDF page for the slot layout.
          // Printer safe inset is applied inside each slot, not around the whole sheet.
          const sheetX = 0;
          const sheetY = 0;
          const sheetWidth = pdfWidth;
          const sheetHeight = pdfHeight;

          const slotWidth = isPortraitMini ? sheetWidth / 2 : sheetWidth;
          const slotHeight = isPortraitMini ? sheetHeight : sheetHeight / 2;

          const copySafeInset = printerSafe ? printerSafeInset : 0;
          const sourceRatio = flattenedImage.width / flattenedImage.height;

          for (let copyIndex = 0; copyIndex < 2; copyIndex++) {
            const slotX = isPortraitMini
              ? sheetX + copyIndex * slotWidth
              : sheetX;

            const slotY = isPortraitMini
              ? sheetY
              : sheetY + copyIndex * slotHeight;

            const safeSlotX = slotX + copySafeInset;
            const safeSlotY = slotY + copySafeInset;
            const safeSlotWidth = Math.max(1, slotWidth - copySafeInset * 2);
            const safeSlotHeight = Math.max(1, slotHeight - copySafeInset * 2);

            let drawWidth = safeSlotWidth;
            let drawHeight = drawWidth / sourceRatio;

            if (drawHeight > safeSlotHeight) {
              drawHeight = safeSlotHeight;
              drawWidth = drawHeight * sourceRatio;
            }

            const drawX = safeSlotX + (safeSlotWidth - drawWidth) / 2;
            const drawY = safeSlotY + (safeSlotHeight - drawHeight) / 2;

            pdf.addImage(
              flattenedDataURL,
              'PNG',
              drawX,
              drawY,
              drawWidth,
              drawHeight
            );
          }

          // ✅ Mini divider line
          // Always draw one faint cutting line between the two mini copies.
          pdf.setDrawColor(190, 190, 190);
          pdf.setLineWidth(0.5);

          if (pdf.setLineDashPattern) {
            pdf.setLineDashPattern([4, 4], 0);
          }

          if (isPortraitMini) {
            // Portrait worksheet = landscape PDF, copies side by side.
            // Divider is vertical.
            const dividerX = sheetX + sheetWidth / 2;
            pdf.line(dividerX, sheetY, dividerX, sheetY + sheetHeight);
          } else {
            // Landscape worksheet = portrait PDF, copies stacked.
            // Divider is horizontal.
            const dividerY = sheetY + sheetHeight / 2;
            pdf.line(sheetX, dividerY, sheetX + sheetWidth, dividerY);
          }

          if (pdf.setLineDashPattern) {
            pdf.setLineDashPattern([], 0);
          }

          continue;
        }

        // ✅ NORMAL / POSTER / LARGE / GIANT MODE
        const pageColumns =
          mode?.orientationAware && page.orientation === 'portrait'
            ? 1
            : columns;

        const pageRows =
          mode?.orientationAware && page.orientation === 'portrait'
            ? 2
            : rows;

        const cropWidth = flattenedImage.width / pageColumns;
        const cropHeight = flattenedImage.height / pageRows;

        for (let row = 0; row < pageRows; row++) {
          for (let col = 0; col < pageColumns; col++) {
            if (hasAddedFirstPage) {
              pdf.addPage('a4', orientation);
            } else {
              hasAddedFirstPage = true;
            }

            const cropCanvas = document.createElement('canvas');
            cropCanvas.width = Math.ceil(cropWidth);
            cropCanvas.height = Math.ceil(cropHeight);

            const ctx = cropCanvas.getContext('2d');

            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, cropCanvas.width, cropCanvas.height);

            ctx.drawImage(
              flattenedImage,
              col * cropWidth,
              row * cropHeight,
              cropWidth,
              cropHeight,
              0,
              0,
              cropCanvas.width,
              cropCanvas.height
            );

            const tileDataURL = cropCanvas.toDataURL('image/png');

            const pdfWidth = pdf.internal.pageSize.getWidth();
            const pdfHeight = pdf.internal.pageSize.getHeight();

            if (printerSafe) {
              const safeX = printerSafeInset;
              const safeY = printerSafeInset;
              const safeWidth = pdfWidth - printerSafeInset * 2;
              const safeHeight = pdfHeight - printerSafeInset * 2;

              pdf.addImage(tileDataURL, 'PNG', safeX, safeY, safeWidth, safeHeight);

              pdf.setDrawColor(180, 180, 180);
              pdf.setLineWidth(0.5);

              if (pdf.setLineDashPattern) {
                pdf.setLineDashPattern([3, 3], 0);
              }

              pdf.rect(safeX, safeY, safeWidth, safeHeight);

              if (pdf.setLineDashPattern) {
                pdf.setLineDashPattern([], 0);
              }
            } else {
              pdf.addImage(tileDataURL, 'PNG', 0, 0, pdfWidth, pdfHeight);
            }
          }
        }
      }

      const filename = sanitizeFilename(
        `${exportFilename || 'canvas'}_${mode?.key || 'advanced'}`,
        'pdf'
      );

      pdf.save(filename);
      alert('Advanced PDF exported!');
    } catch (err) {
      console.error('Advanced PDF export failed:', err);
      alert('Something went wrong with the advanced PDF export.');
    } finally {
      setIsExporting(false);

      setIsGridVisible(originalGridState.isGridVisible);
      setIsRulerGridVisible(originalGridState.isRulerGridVisible);
      setIsQuartersGridVisible(originalGridState.isQuartersGridVisible);
      setIsThirdsGridVisible(originalGridState.isThirdsGridVisible);
      setIsCustomGridVisible(originalGridState.isCustomGridVisible);

      setActivePage(originalPage);
      setIsLoading(false);
      setLoaderMessage('');
    }
  };

  const openAdvancedPDFExport = () => {
    setShowAdvancedPDFExportModal(true);
  };

  const currentPage = pages[activePage] || pages[0] || { shapes: [] };  

  // Determine if all selected shapes are locked
  const areAllSelectedLocked =
    selectedShapeIds.length > 0 &&
    selectedShapeIds.every((id) => {
      const shape = currentPage.shapes.find(
        (s) => s.id.toString() === id
      );
      return shape && shape.locked;
    });

  const lockSelectedShapes = () => {
  if (selectedShapeIds.length === 0) return;

  pushToUndoStack();

  const updatedPages = [...pages];
  const shapes = updatedPages[activePage].shapes;

  // Find all unique groupIds in the selection
  const selectedGroups = new Set();
  selectedShapeIds.forEach((id) => {
    const shape = shapes.find((s) => s.id.toString() === id);
    if (shape?.groupId) {
      selectedGroups.add(shape.groupId);
    }
  });

  const finalSelection = new Set(selectedShapeIds);
  // Add all shapes from the selected groups
  selectedGroups.forEach((groupId) => {
    shapes
      .filter((s) => s.groupId === groupId)
      .forEach((s) => finalSelection.add(s.id.toString()));
  });

  // Decide lock state based on first shape
  const shouldLock = !Array.from(finalSelection).every((id) => {
    const shape = shapes.find((s) => s.id.toString() === id);
    return shape?.locked;
  });

  updatedPages[activePage].shapes = shapes.map((shape) => {
    if (finalSelection.has(shape.id.toString())) {
      return {
        ...shape,
        locked: shouldLock,
      };
    }
    return shape;
  });

  setPages(updatedPages);
};


  
  // Keyboard shortcuts handling
  useEffect(() => {
    const handleKeyDown = (e) => {
      const tag = e.target.tagName;
      const isInput =
        tag === 'INPUT' || tag === 'TEXTAREA' || e.target.isContentEditable;
      if (isInput) return;

      if (e.key === 'Shift') {
        setIsShiftPressed(true);
      }

      if (e.ctrlKey && e.key === 'z') {
        e.preventDefault();
        undo();
      }

      if (e.ctrlKey && e.shiftKey && e.key === 'Z') {
        e.preventDefault();
        redo();
      }

      const selectedWordSearchShape =
        selectedShapeIds.length === 1
          ? pages[activePage].shapes.find(
              (shape) =>
                shape.id.toString() === selectedShapeIds[0] &&
                shape.type === 'wordSearch' &&
                !shape.locked
            )
          : null;

      const hasSelectedWordSearchCell =
        selectedWordSearchShape &&
        selectedWordSearchShape.wordSearchSelectedCellIndex !== null &&
        selectedWordSearchShape.wordSearchSelectedCellIndex !== undefined;

      const pressEnterToAdvance =
      selectedWordSearchShape?.wordSearchPressEnterToAdvance || false;  

      if (hasSelectedWordSearchCell && e.key === 'Enter') {
        e.preventDefault();

        if (pressEnterToAdvance) {
          pushToUndoStack();
          updateSelectedWordSearchCellLetter(
            selectedWordSearchShape.wordSearchLetters?.[
              selectedWordSearchShape.wordSearchSelectedCellIndex
            ] || '',
            { moveNext: true }
          );
        }

        return;
      }

      if ((e.key === 'Delete' || e.key === 'Backspace') && hasSelectedWordSearchCell) {
        e.preventDefault();
        pushToUndoStack();
        const currentValue =
          selectedWordSearchShape.wordSearchLetters?.[
            selectedWordSearchShape.wordSearchSelectedCellIndex
          ] || '';

        updateSelectedWordSearchCellLetter(currentValue.slice(0, -1));
        return;
      }

      if (e.key === 'Delete' || e.key === 'Backspace') {
        e.preventDefault();
        deleteSelectedShapes();
      }

      if (
        hasSelectedWordSearchCell &&
        e.key.length === 1 &&
        !e.ctrlKey &&
        !e.metaKey &&
        !e.altKey
      ) {
        e.preventDefault();

        pushToUndoStack();
          const currentValue =
            selectedWordSearchShape.wordSearchLetters?.[
              selectedWordSearchShape.wordSearchSelectedCellIndex
            ] || '';

          if (pressEnterToAdvance) {
            // ✅ APPEND instead of replace
            updateSelectedWordSearchCellLetter(currentValue + e.key);
          } else {
            // original behaviour
            updateSelectedWordSearchCellLetter(e.key, { moveNext: true });
          }
        return;
      }

      if (e.ctrlKey && e.key === 'c') {
        e.preventDefault();
        copySelectedShapes();
      }

      // ✅ Paste in place (no offset)
      if (e.ctrlKey && e.shiftKey && e.key.toLowerCase() === 'v') {
        e.preventDefault();
        handlePaste(e, { inPlace: true });
        return;
      }


      if (e.ctrlKey && e.key === 'v') {
        e.preventDefault();
        handlePaste(e, { inPlace: false });
      }
    

      if (e.ctrlKey && e.key === 's') {
        e.preventDefault();
        saveCanvas();
      }

      if (e.key === '=') {
        e.preventDefault();
        handleZoomIn();
      }

      if (e.key === '+') {
        e.preventDefault();
        handleZoomIn();
      }

      if (e.key === '-') {
        e.preventDefault();
        handleZoomOut();
      }

      if (!e.ctrlKey && !e.metaKey && !e.altKey) {
        switch (e.key.toLowerCase()) {
          case 'g':
            handleToolClick('hand');
            break;
          case 'v':
            handleToolClick('select');
            break;
          case 's':
            handleToolClick('shape');
            break;
          case 'l':
            handleToolClick('line');
            break;
          case 'p':
            handleToolClick('pencil');
            break;
          case 't':
            handleToolClick('text');
            break;
          case 'n':
            handleToolClick('pen'); // ✅ new shortcut
            break;
          case 'w':
            handleToolClick('workspaceInfo');
            break;
          case 'i':
            setShowImageLibraryModal(true);
            break;
          case 'z':
            handleToolClick('zoom');
            break;
          case 'a':
            addPage();
            break;
          default:
            break;
        }        
      }

      if (e.altKey && !e.ctrlKey && !e.metaKey) {
        if (e.key === 'ArrowUp') {
          e.preventDefault();
          bringForward();
        }
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          sendBack();
        }
      }

      if (!e.ctrlKey && !e.metaKey && !e.altKey) {
        const moveAmount = e.shiftKey ? 10 : 1;
      
        const directions = {
          ArrowUp: { dx: 0, dy: -moveAmount },
          ArrowDown: { dx: 0, dy: moveAmount },
          ArrowLeft: { dx: -moveAmount, dy: 0 },
          ArrowRight: { dx: moveAmount, dy: 0 },
        };
      
        if (e.key in directions) {
          e.preventDefault();
      
          const { dx, dy } = directions[e.key];
      
          const selected = pages[activePage].shapes.filter(
            (shape) => selectedShapeIds.includes(shape.id.toString()) && !shape.locked
          );
      
          if (selected.length === 0) return;
      
          pushToUndoStack();
      
          const updatedPages = [...pages];
      
          // Group shapes by groupId or treat individually
          const groups = {};
          selected.forEach((shape) => {
            const key = shape.groupId || shape.id;
            if (!groups[key]) groups[key] = [];
            groups[key].push(shape);
          });
      
          Object.values(groups).forEach((group) => {
            group.forEach((shape) => {
              const idx = updatedPages[activePage].shapes.findIndex(
                (s) => s.id === shape.id
              );
              updatedPages[activePage].shapes[idx].x += dx;
              updatedPages[activePage].shapes[idx].y += dy;
            });
          });
      
          setPages(updatedPages);
        }
      }
      

    };

    const handleKeyUp = (e) => {
      if (e.key === 'Shift') {
        setIsShiftPressed(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    const handleWheel = (e) => {
    const canvasContainer = document.getElementById('canvas-container');

    // ✅ If gesture was meant to zoom the page (trackpad pinch), block it
    if (e.ctrlKey) {
      e.preventDefault();
    }

    // ✅ Only zoom canvas if it's inside our canvas area
    if (!canvasContainer?.contains(e.target)) {
      return;
    }

    e.preventDefault(); // Also prevent canvas area from scrolling

    const ZOOM_SENSITIVITY = 0.001; // 🔥 adjust this later if needed

    const delta = -e.deltaY;

    // Apply smooth zoom instead of step zoom
    setScale((prevScale) => {
      const newScale = prevScale + delta * ZOOM_SENSITIVITY;

      // Clamp zoom so it doesn’t break
      return Math.max(0.2, Math.min(5, newScale));
    });
  };



    window.addEventListener('wheel', handleWheel, { passive: false });


    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      window.removeEventListener('wheel', handleWheel);
    };
  }, [
    selectedShapeIds,
    canPaste,
    scale,
    panOffset,
    pages,
    activePage,
    selectedTool,
  ]);

  useEffect(() => {
  const handleKeyDown = (e) => {
      const tag = e.target?.tagName;
      const isTypingInField =
        tag === 'INPUT' ||
        tag === 'TEXTAREA' ||
        e.target?.isContentEditable;

      // Ignore if typing anywhere editable
      if (isEditingText || isTypingInField) return;

      if (e.code === 'Space') {
        e.preventDefault();

        if (selectedTool !== 'text') {
          setIsMiddleGrabbing(true);
        }
      }
    };

    const handleKeyUp = (e) => {
      const tag = e.target?.tagName;
      const isTypingInField =
        tag === 'INPUT' ||
        tag === 'TEXTAREA' ||
        e.target?.isContentEditable;

      if (isEditingText || isTypingInField) return;

      if (e.code === 'Space') {
        setIsMiddleGrabbing(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [selectedTool, isEditingText]);

  useEffect(() => {
    const handleMouseDown = (e) => {
      if (e.button === 1) { // middle mouse
        e.preventDefault();
        isMiddleMouseDownRef.current = true;
        setIsMiddleGrabbing(true); // 👈 new
        middleMouseStartRef.current = {
          x: e.clientX,
          y: e.clientY,
          panX: panOffset.x,
          panY: panOffset.y,
        };
      }
    };

    const handleMouseMove = (e) => {
      if (isMiddleMouseDownRef.current) {
        const dx = e.clientX - middleMouseStartRef.current.x;
        const dy = e.clientY - middleMouseStartRef.current.y;
        setPanOffset({
          x: middleMouseStartRef.current.panX + dx,
          y: middleMouseStartRef.current.panY + dy,
        });
      }
    };

    const handleMouseUp = (e) => {
      if (e.button === 1) {
        isMiddleMouseDownRef.current = false;
        setIsMiddleGrabbing(false); // 👈 new
      }
    };

    window.addEventListener('mousedown', handleMouseDown);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousedown', handleMouseDown);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [panOffset]);



  // Undo and redo functions
  const pushToUndoStack = () => {
    console.log('Pushing to undo stack');

    if (propertyHistoryTimerRef.current) {
      window.clearTimeout(propertyHistoryTimerRef.current);
      propertyHistoryTimerRef.current = null;
    }
    propertyHistoryKeyRef.current = null;

    const serializedPages = JSON.stringify(pages);
    const signature = `${activePage}:${serializedPages}`;
    const lastSnapshot = undoStack.current[undoStack.current.length - 1];

    // Controls that save on focus and again on change can request the same
    // snapshot twice. Keep only one real undo step for that state.
    if (lastSnapshot?._signature === signature) {
      redoStack.current = [];
      return;
    }

    const snapshot = {
      pages: JSON.parse(serializedPages),
      activePage,
      _signature: signature,
    };

    undoStack.current.push(snapshot);

    if (undoStack.current.length > 50) {
      undoStack.current.shift();
    }

    redoStack.current = [];
    console.log(`Undo Stack Size: ${undoStack.current.length}`);
    console.log(`Redo Stack Size: ${redoStack.current.length}`);
  };

  const recordPropertyChangeForUndo = (scope, property) => {
    // Changing defaults for the next object is not a canvas edit and should
    // not create an Undo step.
    if (selectedShapeIds.length === 0) return;

    const selectionKey = selectedShapeIds.slice().sort().join(',');
    const historyKey = `${scope}:${property}:${activePage}:${selectionKey}`;

    if (propertyHistoryKeyRef.current !== historyKey) {
      pushToUndoStack();
      propertyHistoryKeyRef.current = historyKey;
    }

    if (propertyHistoryTimerRef.current) {
      window.clearTimeout(propertyHistoryTimerRef.current);
    }

    propertyHistoryTimerRef.current = window.setTimeout(() => {
      propertyHistoryKeyRef.current = null;
      propertyHistoryTimerRef.current = null;
    }, 500);
  };

  const undo = () => {
    if (undoStack.current.length === 0) return;

    if (propertyHistoryTimerRef.current) {
      window.clearTimeout(propertyHistoryTimerRef.current);
      propertyHistoryTimerRef.current = null;
    }
    propertyHistoryKeyRef.current = null;

    const previousState = undoStack.current.pop();

    const serializedCurrentPages = JSON.stringify(pages);
    const currentState = {
      pages: JSON.parse(serializedCurrentPages),
      activePage,
      _signature: `${activePage}:${serializedCurrentPages}`,
    };

    redoStack.current.push(currentState);

    setPages(previousState.pages);
    setActivePage(previousState.activePage);
    setSelectedShapeIds([]);
  };

  const redo = () => {
    if (redoStack.current.length === 0) return;

    if (propertyHistoryTimerRef.current) {
      window.clearTimeout(propertyHistoryTimerRef.current);
      propertyHistoryTimerRef.current = null;
    }
    propertyHistoryKeyRef.current = null;

    const nextState = redoStack.current.pop();

    const serializedCurrentPages = JSON.stringify(pages);
    const currentState = {
      pages: JSON.parse(serializedCurrentPages),
      activePage,
      _signature: `${activePage}:${serializedCurrentPages}`,
    };

    undoStack.current.push(currentState);

    setPages(nextState.pages);
    setActivePage(nextState.activePage);
    setSelectedShapeIds([]);
  };

  const flipSelectedHorizontally = () => {
    if (selectedShapeIds.length === 0) return;
    pushToUndoStack();
  
    const shapes = pages[activePage].shapes;
    const selected = shapes.filter(shape => selectedShapeIds.includes(shape.id.toString()) && !shape.locked);
  
    const centerX = selected.reduce((sum, shape) => sum + shape.x, 0) / selected.length;
  
    const updatedPages = [...pages];
    updatedPages[activePage].shapes = shapes.map((shape) => {
      if (selectedShapeIds.includes(shape.id.toString()) && !shape.locked) {
        return {
          ...shape,
          x: centerX - (shape.x - centerX),
          scaleX: (shape.scaleX || 1) * -1,
        };
      }
      return shape;
    });
  
    setPages(updatedPages);
  };
  
  
  const flipSelectedVertically = () => {
    if (selectedShapeIds.length === 0) return;
    pushToUndoStack();
  
    const shapes = pages[activePage].shapes;
    const selected = shapes.filter(shape => selectedShapeIds.includes(shape.id.toString()) && !shape.locked);
  
    const centerY = selected.reduce((sum, shape) => sum + shape.y, 0) / selected.length;
  
    const updatedPages = [...pages];
    updatedPages[activePage].shapes = shapes.map((shape) => {
      if (selectedShapeIds.includes(shape.id.toString()) && !shape.locked) {
        return {
          ...shape,
          y: centerY - (shape.y - centerY),
          scaleY: (shape.scaleY || 1) * -1,
        };
      }
      return shape;
    });
  
    setPages(updatedPages);
  };

  const applyBucketToGridCell = (shapeId, cellIndex, options = {}) => {
    const { skipUndo = false } = options;

    const shape = pages[activePage].shapes.find(
      (s) => s.id.toString() === shapeId.toString()
    );

    if (!shape || shape.locked || shape.type !== 'grid') return;

    if (!skipUndo) {
      pushToUndoStack();
    }

    const updatedPages = [...pages];
    updatedPages[activePage].shapes = updatedPages[activePage].shapes.map((s) => {
      if (s.id.toString() !== shapeId.toString()) return s;

      const rows = Math.max(1, parseInt(s.gridRows, 10) || 1);
      const columns = Math.max(1, parseInt(s.gridColumns, 10) || 1);
      const total = rows * columns;
      const defaultColor = s.fillColor || '#ffffff';

      const nextFills = Array.isArray(s.gridCellFills)
        ? [...s.gridCellFills]
        : buildGridCellFills(rows, columns, defaultColor);

      while (nextFills.length < total) {
        nextFills.push(defaultColor);
      }

      nextFills[cellIndex] = bucketSettings.color;

      return {
        ...s,
        gridCellFills: nextFills,
      };
    });

    setPages(updatedPages);
    setSelectedShapeIds([shapeId.toString()]);
  };

    const applyBucketToCircleSegment = (shapeId, segmentIndex) => {
      const shape = pages[activePage].shapes.find(
        (s) => s.id.toString() === shapeId.toString()
      );

      if (!shape || shape.locked || shape.type !== 'circleSegments') return;

      pushToUndoStack();

      const updatedPages = [...pages];
      updatedPages[activePage].shapes = updatedPages[activePage].shapes.map((s) => {
        if (s.id.toString() !== shapeId.toString()) return s;

        const totalSegments = Math.max(1, parseInt(s.segments, 10) || 1);
        const defaultColor = s.fillColor || '#ffffff';

        const nextFills = Array.isArray(s.circleSegmentFills)
          ? [...s.circleSegmentFills]
          : Array.from({ length: totalSegments }, () => defaultColor);

        while (nextFills.length < totalSegments) {
          nextFills.push(defaultColor);
        }

        nextFills[segmentIndex] = bucketSettings.color;

        return {
          ...s,
          circleSegmentFills: nextFills,
        };
      });

      setPages(updatedPages);
      setSelectedShapeIds([shapeId.toString()]);
    };
  
  const applyBucketToShape = (shapeId) => {
    const shape = pages[activePage].shapes.find(
      (s) => s.id.toString() === shapeId.toString()
    );

    if (!shape || shape.locked) return;

    pushToUndoStack();

    const { color, target } = bucketSettings;

    const updatedPages = [...pages];
    updatedPages[activePage].shapes = updatedPages[activePage].shapes.map((s) => {
      if (s.id.toString() !== shapeId.toString()) return s;

      // TEXT
      if (s.type === 'text') {
        if (target === 'stroke') return s;
        return {
          ...s,
          fill: color,
        };
      }

      // LINES / PENCIL
      if (s.type === 'line' || s.type === 'pencil') {
        if (target === 'fill' || target === 'text') return s;
        return {
          ...s,
          strokeColor: color,
        };
      }

      if (s.type === 'grid') {
        if (target === 'stroke') {
          return {
            ...s,
            strokeEnabled: true,
            strokeColor: color,
          };
        }

        if (target === 'fill') {
          const rows = Math.max(1, parseInt(s.gridRows, 10) || 1);
          const columns = Math.max(1, parseInt(s.gridColumns, 10) || 1);
          const total = rows * columns;

          const newFills = Array.from({ length: total }, () => color);

          return {
            ...s,
            fillEnabled: true,
            fillColor: color,
            gridCellFills: newFills,
          };
        }

        return s;
      }

      // PEN SHAPES
      if (s.type === 'pen') {
        if (target === 'fill') {
          return {
            ...s,
            fillEnabled: true,
            fillColor: color,
          };
        }

        if (target === 'stroke') {
          return {
            ...s,
            strokeEnabled: true,
            strokeColor: color,
          };
        }

        if (target === 'text') return s;

        // auto
        if (s.fillEnabled !== false) {
          return {
            ...s,
            fillEnabled: true,
            fillColor: color,
          };
        }

        return {
          ...s,
          strokeEnabled: true,
          strokeColor: color,
        };
      }

      // SHAPES
      if (
        [
          'rect',
          'roundedRect',
          'circle',
          'semiCircle',
          'triangle',
          'pentagon',
          'hexagon',
          'octagon',
          'star',
          'grid',
          'maze',
          'circleSegments',
          'wordSearch',
        ].includes(s.type)
      ) {
                if (s.type === 'circleSegments') {
          if (target === 'fill') {
            const totalSegments = Math.max(1, parseInt(s.segments, 10) || 1);

            return {
              ...s,
              fillEnabled: true,
              fillColor: color,
              circleSegmentFills: Array.from({ length: totalSegments }, () => color),
            };
          }

          if (target === 'stroke') {
            return {
              ...s,
              strokeEnabled: true,
              strokeColor: color,
            };
          }

          if (target === 'text') return s;

          if (s.fillEnabled !== false) {
            const totalSegments = Math.max(1, parseInt(s.segments, 10) || 1);

            return {
              ...s,
              fillEnabled: true,
              fillColor: color,
              circleSegmentFills: Array.from({ length: totalSegments }, () => color),
            };
          }

          return {
            ...s,
            strokeEnabled: true,
            strokeColor: color,
          };
        }

        if (target === 'fill') {
          return {
            ...s,
            fillEnabled: true,
            fillColor: color,
          };
        }

        if (target === 'stroke') {
          return {
            ...s,
            strokeEnabled: true,
            strokeColor: color,
          };
        }

        if (target === 'text') return s;

        // auto
        if (s.fillEnabled !== false) {
          return {
            ...s,
            fillEnabled: true,
            fillColor: color,
          };
        }

        return {
          ...s,
          strokeEnabled: true,
          strokeColor: color,
        };
      }

      return s;
    });

    setPages(updatedPages);

    // keep selection synced so properties panel updates immediately
    setSelectedShapeIds([shapeId.toString()]);

    const updatedShape = updatedPages[activePage].shapes.find(
      (s) => s.id.toString() === shapeId.toString()
    );

    if (!updatedShape) return;

    if (updatedShape.type === 'text') {
      setTextProperties((prev) => ({
        ...prev,
        fill: updatedShape.fill || prev.fill,
      }));
      return;
    }

    if (
      [
        'rect',
        'roundedRect',
        'circle',
        'semiCircle',
        'triangle',
        'pentagon',
        'hexagon',
        'octagon',
        'star',
        'grid',
        'maze',
        'pen',
        'circleSegments',
        'wordSearch',
      ].includes(updatedShape.type)
    ) {
      setShapeProperties((prev) => ({
        ...prev,
        fillColor: updatedShape.fillColor ?? prev.fillColor,
        strokeColor: updatedShape.strokeColor ?? prev.strokeColor,
        fillEnabled: updatedShape.fillEnabled ?? prev.fillEnabled,
        strokeEnabled: updatedShape.strokeEnabled ?? prev.strokeEnabled,
        arcAngle: updatedShape.arcAngle ?? prev.arcAngle,
        type: updatedShape.type,
      }));
    }
  };

  // Action handlers
  const deleteSelectedShapes = () => {
    if (selectedShapeIds.length === 0) return;
  
    // Identify which selected shapes are locked and which are not
    const lockedShapes = pages[activePage].shapes.filter(
      (shape) => selectedShapeIds.includes(shape.id.toString()) && shape.locked
    );
    const unlockedShapes = pages[activePage].shapes.filter(
      (shape) => selectedShapeIds.includes(shape.id.toString()) && !shape.locked
    );
  
    // If all selected shapes are locked, notify the user and prevent deletion
    if (lockedShapes.length === selectedShapeIds.length) {
      alert('Cannot delete selected shapes because they are locked.');
      return;
    }
  
    // If some selected shapes are locked, notify the user that only unlocked shapes will be deleted
    if (lockedShapes.length > 0) {
      alert('Some selected shapes are locked and could not be deleted.');
    }
  
    // Proceed to delete only the unlocked shapes
    // Proceed to delete only the unlocked shapes
      pushToUndoStack(); // ✅ Add this to register deletion in undo history

      const updatedPages = [...pages];
      updatedPages[activePage].shapes = updatedPages[activePage].shapes.filter(
        (shape) => !(selectedShapeIds.includes(shape.id.toString()) && !shape.locked)
      );
      setPages(updatedPages);
      setSelectedShapeIds([]);
  };

  const alignSelectionVertically = () => {
    if (selectedShapeIds.length < 2) return;
  
    pushToUndoStack();
  
    const shapes = pages[activePage].shapes;
    const selected = shapes.filter(s => selectedShapeIds.includes(s.id.toString()) && !s.locked);
    const updatedPages = [...pages];
  
    const groups = {};
    selected.forEach((shape) => {
      const key = shape.groupId || shape.id;
      if (!groups[key]) groups[key] = [];
      groups[key].push(shape);
    });
  
    const groupCenters = Object.values(groups).map(group => {
      return group.reduce((sum, s) => sum + s.x, 0) / group.length;
    });
    const avgX = groupCenters.reduce((a, b) => a + b, 0) / groupCenters.length;
  
    Object.values(groups).forEach(group => {
      const groupX = group.reduce((sum, s) => sum + s.x, 0) / group.length;
      const deltaX = avgX - groupX;
  
      group.forEach(s => {
        const index = pages[activePage].shapes.findIndex(ss => ss.id === s.id);
        updatedPages[activePage].shapes[index].x += deltaX;
      });
    });
  
    setPages(updatedPages);
  };
  
  
  const alignSelectionHorizontally = () => {
    if (selectedShapeIds.length < 2) return;
  
    pushToUndoStack();
  
    const shapes = pages[activePage].shapes;
    const selected = shapes.filter(s => selectedShapeIds.includes(s.id.toString()) && !s.locked);
    const updatedPages = [...pages];
  
    // 1. Group shapes by groupId (or treat individually if no group)
    const groups = {};
    selected.forEach((shape) => {
      const key = shape.groupId || shape.id;
      if (!groups[key]) groups[key] = [];
      groups[key].push(shape);
    });
  
    // 2. Get center Y of each group
    const groupCenters = Object.values(groups).map(group => {
      return group.reduce((sum, s) => sum + s.y, 0) / group.length;
    });
    const avgY = groupCenters.reduce((a, b) => a + b, 0) / groupCenters.length;
  
    // 3. Move each group as a unit so its center aligns with the average
    Object.values(groups).forEach(group => {
      const groupY = group.reduce((sum, s) => sum + s.y, 0) / group.length;
      const deltaY = avgY - groupY;
  
      group.forEach(s => {
        const index = pages[activePage].shapes.findIndex(ss => ss.id === s.id);
        updatedPages[activePage].shapes[index].y += deltaY;
      });
    });
  
    setPages(updatedPages);
  };
  
  
  const distributeSelectionHorizontally = () => {
    if (selectedShapeIds.length < 3) return;
  
    pushToUndoStack();
  
    const shapes = pages[activePage].shapes;
    const selected = shapes.filter(s => selectedShapeIds.includes(s.id.toString()) && !s.locked);
    const updatedPages = [...pages];
  
    // Group shapes by groupId (or treat individually if no group)
    const groups = {};
    selected.forEach(shape => {
      const key = shape.groupId || shape.id;
      if (!groups[key]) groups[key] = [];
      groups[key].push(shape);
    });
  
    const groupList = Object.values(groups);
    if (groupList.length < 3) return;
  
    const groupBounds = groupList.map(group => {
      const minX = Math.min(...group.map(s => s.x));
      const maxX = Math.max(...group.map(s => s.x));
      return { group, minX, maxX, center: (minX + maxX) / 2 };
    });
  
    groupBounds.sort((a, b) => a.center - b.center);
    const first = groupBounds[0];
    const last = groupBounds[groupBounds.length - 1];
    const totalGap = (last.center - first.center) / (groupBounds.length - 1);
  
    groupBounds.forEach((entry, index) => {
      if (index === 0 || index === groupBounds.length - 1) return;
      const groupCenter = entry.center;
      const deltaX = first.center + totalGap * index - groupCenter;
  
      entry.group.forEach(shape => {
        const indexInShapes = updatedPages[activePage].shapes.findIndex(s => s.id === shape.id);
        updatedPages[activePage].shapes[indexInShapes].x += deltaX;
      });
    });
  
    setPages(updatedPages);
  };
  
  
  const distributeSelectionVertically = () => {
    if (selectedShapeIds.length < 3) return;
  
    pushToUndoStack();
  
    const shapes = pages[activePage].shapes;
    const selected = shapes.filter(s => selectedShapeIds.includes(s.id.toString()) && !s.locked);
    const updatedPages = [...pages];
  
    const groups = {};
    selected.forEach(shape => {
      const key = shape.groupId || shape.id;
      if (!groups[key]) groups[key] = [];
      groups[key].push(shape);
    });
  
    const groupList = Object.values(groups);
    if (groupList.length < 3) return;
  
    const groupBounds = groupList.map(group => {
      const minY = Math.min(...group.map(s => s.y));
      const maxY = Math.max(...group.map(s => s.y));
      return { group, minY, maxY, center: (minY + maxY) / 2 };
    });
  
    groupBounds.sort((a, b) => a.center - b.center);
    const first = groupBounds[0];
    const last = groupBounds[groupBounds.length - 1];
    const totalGap = (last.center - first.center) / (groupBounds.length - 1);
  
    groupBounds.forEach((entry, index) => {
      if (index === 0 || index === groupBounds.length - 1) return;
      const groupCenter = entry.center;
      const deltaY = first.center + totalGap * index - groupCenter;
  
      entry.group.forEach(shape => {
        const indexInShapes = updatedPages[activePage].shapes.findIndex(s => s.id === shape.id);
        updatedPages[activePage].shapes[indexInShapes].y += deltaY;
      });
    });
  
    setPages(updatedPages);
  };
  
  
  const groupSelectedShapes = () => {
    console.log('🔧 Grouping shapes...'); // ✅ Add this for debug
  
    if (selectedShapeIds.length < 2) return;
  
    pushToUndoStack();
  
    const newGroupId = `group-${Date.now()}`;
    const updatedPages = [...pages];
  
    updatedPages[activePage].shapes = updatedPages[activePage].shapes.map(shape => {
      if (selectedShapeIds.includes(shape.id.toString()) && !shape.locked) {
        return { ...shape, groupId: newGroupId };
      }
      return shape;
    });
  
    setPages(updatedPages);
  };
  
  
  const ungroupSelectedShapes = () => {
    console.log('🔧 Ungrouping shapes...');

    pushToUndoStack();
  
    const updatedPages = [...pages];
    updatedPages[activePage].shapes = updatedPages[activePage].shapes.map(shape => {
      if (selectedShapeIds.includes(shape.id.toString())) {
        const newShape = { ...shape };
        delete newShape.groupId;
        return newShape;
      }
      return shape;
    });
  
    setPages(updatedPages);
  };

  // Turn the current shape stack into “units” (a group = 1 unit, lone shape = 1 unit)
const buildLayerUnits = (shapes) => {
  const units = [];
  const seen  = new Set();

  shapes.forEach((shape) => {
    if (shape.groupId) {
      if (seen.has(shape.groupId)) return;
      units.push(shapes.filter((s) => s.groupId === shape.groupId));
      seen.add(shape.groupId);
    } else {
      units.push([shape]);
    }
  });

  return units;          // e.g. [ [groupA-sh1, sh2], [singleX], [groupB-sh1,sh2,sh3] ]
};
  

const bringForward = () => {
  if (!selectedShapeIds.length) return;

  const page        = pages[activePage];
  const shapes      = page.shapes;
  const idSet       = new Set(selectedShapeIds);
  const lockedCount = shapes.filter((s) => idSet.has(s.id.toString()) && s.locked).length;

  if (lockedCount === selectedShapeIds.length) {
    alert('Cannot move selected shapes because they are locked.');
    return;
  }
  if (lockedCount) alert('Some selected shapes are locked and could not be moved forward.');

  pushToUndoStack();
  const units = buildLayerUnits(shapes);

  const unitIsSelected = (unit) => unit.some((s) => idSet.has(s.id.toString()) && !s.locked);

  // walk from top-2 downwards, swapping selected unit with the one above it
  for (let i = units.length - 2; i >= 0; i--) {
    if (unitIsSelected(units[i]) && !unitIsSelected(units[i + 1])) {
      [units[i], units[i + 1]] = [units[i + 1], units[i]];
      i--;          // skip the unit we just hopped over
    }
  }

  pages[activePage].shapes = units.flat();
  setPages([...pages]);
};



  const sendBack = () => {
    if (!selectedShapeIds.length) return;

    const page        = pages[activePage];
    const shapes      = page.shapes;
    const idSet       = new Set(selectedShapeIds);
    const lockedCount = shapes.filter((s) => idSet.has(s.id.toString()) && s.locked).length;

    if (lockedCount === selectedShapeIds.length) {
      alert('Cannot move selected shapes because they are locked.');
      return;
    }
    if (lockedCount) alert('Some selected shapes are locked and could not be moved backward.');

    pushToUndoStack();
    const units = buildLayerUnits(shapes);

    const unitIsSelected = (unit) => unit.some((s) => idSet.has(s.id.toString()) && !s.locked);

    // walk from index 1 upwards, swapping selected unit with the one below it
    for (let i = 1; i < units.length; i++) {
      if (unitIsSelected(units[i]) && !unitIsSelected(units[i - 1])) {
        [units[i - 1], units[i]] = [units[i], units[i - 1]];
        i++;          // skip the unit we just hopped over
      }
    }

    pages[activePage].shapes = units.flat();
    setPages([...pages]);
  };

  const bringToFront = () => {
    if (!selectedShapeIds.length) return;

    const page = pages[activePage];
    const shapes = page.shapes;
    const idSet = new Set(selectedShapeIds);

    const lockedCount = shapes.filter(
      (s) => idSet.has(s.id.toString()) && s.locked
    ).length;

    if (lockedCount === selectedShapeIds.length) {
      alert('Cannot move selected shapes because they are locked.');
      return;
    }

    if (lockedCount) {
      alert('Some selected shapes are locked and could not be moved to front.');
    }

    pushToUndoStack();

    const units = buildLayerUnits(shapes);

    const selectedUnits = units.filter((unit) =>
      unit.some((s) => idSet.has(s.id.toString()) && !s.locked)
    );

    const otherUnits = units.filter(
      (unit) => !unit.some((s) => idSet.has(s.id.toString()) && !s.locked)
    );

    const updatedPages = [...pages];

    updatedPages[activePage] = {
      ...updatedPages[activePage],
      shapes: [...otherUnits, ...selectedUnits].flat(),
    };

    setPages(updatedPages);
  };


  const sendToBack = () => {
    if (!selectedShapeIds.length) return;

    const page = pages[activePage];
    const shapes = page.shapes;
    const idSet = new Set(selectedShapeIds);

    const lockedCount = shapes.filter(
      (s) => idSet.has(s.id.toString()) && s.locked
    ).length;

    if (lockedCount === selectedShapeIds.length) {
      alert('Cannot move selected shapes because they are locked.');
      return;
    }

    if (lockedCount) {
      alert('Some selected shapes are locked and could not be moved to back.');
    }

    pushToUndoStack();

    const units = buildLayerUnits(shapes);

    const selectedUnits = units.filter((unit) =>
      unit.some((s) => idSet.has(s.id.toString()) && !s.locked)
    );

    const otherUnits = units.filter(
      (unit) => !unit.some((s) => idSet.has(s.id.toString()) && !s.locked)
    );

    const updatedPages = [...pages];

    updatedPages[activePage] = {
      ...updatedPages[activePage],
      shapes: [...selectedUnits, ...otherUnits].flat(),
    };

    setPages(updatedPages);
  };


  // Tool click handlers
  const handleToolClick = (tool) => {
    setSelectedTool(tool);
    setSelectedShapeIds([]);
    setIsMeasurementRulerSelected(false);
    setSelectionRect(null);
    setIsEditingText(false);
  };
  const prepareToolTourStep = (tool) => {
    setIsPropertiesCollapsed(false);
    handleToolClick(tool);
  };

  const toolsTourSteps = [
    {
      selectors: [
        '[data-tour="tool-workspace-info"]',
        '[data-tour="editor-properties"]',
      ],
      placement: 'canvas-center',
      eyebrow: 'Workspace',
      title: 'Workspace Info',
      description: (
        <p>
          Change the canvas orientation, workspace appearance, visible
          guides and other worksheet settings from here.
        </p>
      ),
      onEnter: () => prepareToolTourStep('workspaceInfo'),
    },
    {
      selectors: [
        '[data-tour="tool-hand"]',
        '[data-tour="editor-properties"]',
      ],
      placement: 'canvas-center',
      eyebrow: 'Navigation',
      title: 'Grab Tool',
      description: (
        <p>
          Move around the workspace without moving anything on your
          worksheet. You can also hold the spacebar or use the middle
          mouse button to temporarily grab the workspace.
        </p>
      ),
      onEnter: () => prepareToolTourStep('hand'),
    },
    {
      selectors: [
        '[data-tour="tool-select"]',
        '[data-tour="editor-properties"]',
      ],
      placement: 'canvas-center',
      eyebrow: 'Editing',
      title: 'Select Tool',
      description: (
        <p>
          Select objects so you can move, resize, rotate, duplicate,
          group, lock or edit them.
        </p>
      ),
      onEnter: () => prepareToolTourStep('select'),
    },
    {
      selectors: [
        '[data-tour="tool-shape"]',
        '[data-tour="editor-properties"]',
      ],
      placement: 'canvas-center',
      eyebrow: 'Creation',
      title: 'Shape Tool',
      description: (
        <p>
          Add basic shapes, grids, segmented circles, mazes and word
          searches. Choose the shape and adjust its appearance in the
          Tool Properties panel.
        </p>
      ),
      onEnter: () => prepareToolTourStep('shape'),
    },
    {
      selectors: [
        '[data-tour="tool-line"]',
        '[data-tour="editor-properties"]',
      ],
      placement: 'canvas-center',
      eyebrow: 'Creation',
      title: 'Line Tool',
      description: (
        <p>
          Draw straight lines and arrows. You can change the width,
          colour, opacity and line style in the Tool Properties panel.
        </p>
      ),
      onEnter: () => prepareToolTourStep('line'),
    },
    {
      selectors: [
        '[data-tour="tool-pencil"]',
        '[data-tour="editor-properties"]',
      ],
      placement: 'canvas-center',
      eyebrow: 'Drawing',
      title: 'Pencil Tool',
      description: (
        <p>
          Draw freehand lines directly on the worksheet. Adjust the
          smoothing, width, colour and line style before drawing.
        </p>
      ),
      onEnter: () => prepareToolTourStep('pencil'),
    },
    {
      selectors: [
        '[data-tour="tool-pen"]',
        '[data-tour="editor-properties"]',
      ],
      placement: 'canvas-center',
      eyebrow: 'Drawing',
      title: 'Pen Tool',
      description: (
        <>
          <p>
            Create accurate custom shapes by placing individual points.
          </p>

          <p>
            Turn on bendy lines to create curves, then edit the points
            and handles after drawing.
          </p>
        </>
      ),
      onEnter: () => prepareToolTourStep('pen'),
    },
    {
      selectors: [
        '[data-tour="tool-text"]',
        '[data-tour="editor-properties"]',
      ],
      placement: 'canvas-center',
      eyebrow: 'Creation',
      title: 'Text Tool',
      description: (
        <p>
          Click the worksheet to add text. Use the Tool Properties panel
          to change the wording, font, size, colour, alignment and style.
        </p>
      ),
      onEnter: () => prepareToolTourStep('text'),
    },
    {
      selectors: [
        '[data-tour="tool-bucket"]',
        '[data-tour="editor-properties"]',
      ],
      placement: 'canvas-center',
      eyebrow: 'Colouring',
      title: 'Paint Bucket',
      description: (
        <p>
          Choose a colour, then click a compatible object or grid cell
          to recolour it quickly.
        </p>
      ),
      onEnter: () => prepareToolTourStep('bucket'),
    },
    {
      selectors: [
        '[data-tour="tool-add-image"]',
        '[data-tour="editor-properties"]',
      ],
      placement: 'canvas-center',
      eyebrow: 'Images',
      title: 'Add Image',
      description: (
        <>
          <p>
            Upload your own image or choose one from EDU Groove’s built-in
            Image Library.
          </p>

          <p>
            You can also copy an AI Image Style prompt into any AI image
            generator, then copy and paste the finished image directly
            back into EDU Groove.
          </p>
        </>
      ),
      onEnter: () => prepareToolTourStep('addImage'),
    },
    {
      selectors: [
        '[data-tour="tool-zoom"]',
        '[data-tour="editor-properties"]',
      ],
      placement: 'canvas-center',
      eyebrow: 'Navigation',
      title: 'Zoom Tool',
      description: (
        <p>
          Zoom in or out to work on small details or view more of the
          worksheet. You can also use the plus and minus keyboard keys.
        </p>
      ),
      onEnter: () => prepareToolTourStep('zoom'),
    },
    {
      selectors: [
        '[data-tour="tool-add-page"]',
        '[data-tour="editor-properties"]',
      ],
      placement: 'canvas-center',
      eyebrow: 'Pages',
      title: 'Add Page',
      description: (
        <p>
          Add a blank page or duplicate the current page to create
          multi-page activities.
        </p>
      ),
      onEnter: () => prepareToolTourStep('addPage'),
    },
    {
      selectors: [
        '[data-tour="tool-save"]',
        '[data-tour="editor-properties"]',
      ],
      placement: 'canvas-center',
      eyebrow: 'Saving and Exporting',
      title: 'Save and Export',
      description: (
        <>
          <p>
            Download your editable project as a .groove file and upload it
            again whenever you want to continue working.
          </p>

          <p>
            You can also upload a pre-created PDF, export a finished
            class-ready PDF, or use Advanced PDF Export to create wall art,
            posters and giant versions of your activities.
          </p>
        </>
      ),
      onEnter: () => prepareToolTourStep('save'),
    },
    
    {
      selector: '[data-tour="editor-top-menu"]',
      placement: 'bottom',
      eyebrow: 'Editing',
      title: 'Top Menu Bar',
      description: (
        <p>
          Use these buttons for undo, redo, delete, copy, paste, object
          order, copying across pages, locking and Edit Mode.
        </p>
      ),
      onEnter: () => {
        setIsPropertiesCollapsed(false);
        handleToolClick('select');
      },
    },
    {
      selector: '[data-tour="editor-pages"]',
      placement: 'top-wide-gap',
      eyebrow: 'Multiple Pages',
      title: 'Page Controls',
      description: (
        <>
          <p>
            Change pages, add new pages, delete pages and drag page tabs
            to rearrange their order.
          </p>

          <p>
            You can reopen this tutorial at any time using the
            question-mark button in the top menu.
          </p>
        </>
      ),
      nextLabel: 'Finish Tutorial',
      onEnter: () => {
        setIsPropertiesCollapsed(false);
        handleToolClick('select');
      },
    },
  ];

  const openToolsTour = () => {
    setIsEditorTourOpen(false);
    setEditorTourStep(0);

    setToolsTourStep(0);
    setIsToolsTourOpen(true);
  };

  const closeToolsTour = () => {
    setIsToolsTourOpen(false);
    setToolsTourStep(0);

    setSelectedTool('select');
    setSelectedShapeIds([]);
    setIsMeasurementRulerSelected(false);
  };

  const finishToolsTour = () => {
    localStorage.setItem(
      `edugroove_tools_tour_completed_${tourStorageUserId}`,
      'true'
    );

    setIsToolsTourOpen(false);
    setToolsTourStep(0);

    setSelectedTool('select');
    setSelectedShapeIds([]);
    setIsMeasurementRulerSelected(false);
  };

  const handleMouseEnter = (tool) => {
    setHoveredTool(tool);
  };

  const handleMouseLeave = () => {
    setHoveredTool(null);
  };

  const togglePropertiesPanel = () => {
    setIsPropertiesCollapsed(!isPropertiesCollapsed);
  };

  const toggleSidebar = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  // Canvas Pan and Zoom Handlers
  const handleCanvasMouseDownDrag = (e) => {
    if (selectedTool === 'hand' || isMiddleGrabbing) {
      setDragging(true);
      setOffset({
        startX: e.clientX,
        startY: e.clientY,
        initialX: panOffset.x,
        initialY: panOffset.y,
      });
    }
  };

  const handleCanvasMouseMoveDrag = (e) => {
    if (dragging) {
      const dx = e.clientX - offset.startX;
      const dy = e.clientY - offset.startY;
      setPanOffset({
        x: offset.initialX + dx,
        y: offset.initialY + dy,
      });
    }
  };

  const handleCanvasMouseUpDrag = () => {
    if (dragging) {
      setDragging(false);
    }
  };

  // Stage Mouse Handlers
  const handleStageMouseDown = (e) => {
    if (e?.evt?.button === 1 || e?.button === 1) return;
    const stage = e.target.getStage();
    const pos = stage.getPointerPosition();

    if (selectedTool === 'bucket') {
      return;
    }
  
    if (selectedTool === 'pencil') {
      pushToUndoStack();

      console.log("Drawing new pencil line with tension:", pencilProperties.tension);

      const newPencilLine = {
        id: Date.now(),
        type: 'pencil',
        rawPoints: [pos.x, pos.y],
        points: [pos.x, pos.y],
        x: 0,
        y: 0,
        rotation: 0,
        scaleX: 1,
        scaleY: 1,
        strokeWidth: pencilProperties.strokeWidth,
        strokeColor: pencilProperties.strokeColor,
        opacity: pencilProperties.opacity,
        tension: pencilProperties.tension,
        lineCap: 'round',
        lineJoin: 'round',
        dashEnabled: pencilProperties.dashEnabled,
        dash: pencilProperties.dashEnabled ? pencilProperties.dash : [],
        locked: false,
      };
  
      const updatedPages = [...pages];
      updatedPages[activePage].shapes.push(newPencilLine);
      setPages(updatedPages);
      setDrawing(true);
    } else if (selectedTool === 'line') {
      pushToUndoStack();
      const newLine = {
        id: Date.now(),
        type: 'line',
        points: [pos.x, pos.y, pos.x, pos.y],
        x: 0,
        y: 0,
        rotation: 0,
        scaleX: 1,
        scaleY: 1,
        strokeWidth: lineProperties.strokeWidth,
        strokeColor: lineProperties.strokeColor,
        opacity: lineProperties.opacity ?? 1,
        dashEnabled: lineProperties.dashEnabled,
        dash: lineProperties.dashEnabled ? lineProperties.dash : [],
        locked: false,
      };
  
      const updatedPages = [...pages];
      updatedPages[activePage].shapes.push(newLine);
      setPages(updatedPages);
      setDrawing(true);
    } else if (selectedTool === 'shape') {
      pushToUndoStack();
      let newShape = {
        id: Date.now(),
        type: selectedShapeType,
        initialX: pos.x, // Store initial X position
        initialY: pos.y, // Store initial Y position
        x: pos.x,
        y: pos.y,
        rotation: 0,
        scaleX: 1,
        scaleY: 1,
        strokeWidth: shapeProperties.strokeWidth,
        strokeColor: shapeProperties.strokeColor,
        fillColor: shapeProperties.fillColor,
        fillStyle: shapeProperties.fillStyle || 'solid',
        fillSecondaryColor: shapeProperties.fillSecondaryColor || '#F2D6A7',
        fillPatternSize: shapeProperties.fillPatternSize || 10,
        fillPatternSpacing: shapeProperties.fillPatternSpacing ?? 8,
        fillPatternRotation: shapeProperties.fillPatternRotation || 0,
        opacity: shapeProperties.opacity,
        strokeStyle: shapeProperties.strokeStyle || 'solid',

      strokeWaveSize: shapeProperties.strokeWaveSize || 8,
      strokeWaveFrequency: shapeProperties.strokeWaveFrequency || 6,

      dashEnabled:
        shapeProperties.strokeStyle === 'dashed' ||
        shapeProperties.strokeStyle === 'dotted',

      dash:
        shapeProperties.strokeStyle === 'dashed'
          ? (shapeProperties.dash?.length ? shapeProperties.dash : [10, 5])
          : shapeProperties.strokeStyle === 'dotted'
            ? (shapeProperties.dash?.length ? shapeProperties.dash : [0, Math.max(4, shapeProperties.strokeWidth * 2.5)])
            : [],
        strokeEnabled:
          selectedTool === 'shape'
            ? shapeProperties.strokeEnabled
            : true,
        fillEnabled:
          selectedTool === 'shape'
            ? shapeProperties.fillEnabled
            : true,
        locked: false,
      };
  
      // Initialize shape-specific properties
      switch (selectedShapeType) {
        case 'rect':
        case 'roundedRect':
          newShape = {
            ...newShape,
            width: 0,
            height: 0,
            cornerRadius: shapeProperties.cornerRadius || 10,
          };
          break;
        case 'grid':
          newShape = {
            ...newShape,
            width: 0,
            height: 0,
            gridRows: shapeProperties.gridRows || 3,
            gridColumns: shapeProperties.gridColumns || 3,
            fillEnabled:
              shapeProperties.fillEnabled !== undefined
                ? shapeProperties.fillEnabled
                : true,
            gridCellFills: buildGridCellFills(
              shapeProperties.gridRows || 3,
              shapeProperties.gridColumns || 3,
              shapeProperties.fillColor || '#ffffff'
            ),
          };
          break;
        case 'maze':
          newShape = {
            ...newShape,
            width: 0,
            height: 0,
            mazeRows: shapeProperties.mazeRows || 8,
            mazeColumns: shapeProperties.mazeColumns || 8,
            mazeExits: shapeProperties.mazeExits || 2,
            mazeComplexity: shapeProperties.mazeComplexity || 3,
            mazeWalls: [],
          };
          break;
        case 'wordSearch':
          newShape = {
            ...newShape,
            width: 0,
            height: 0,
            wordSearchRows: shapeProperties.wordSearchRows || 5,
            wordSearchColumns: shapeProperties.wordSearchColumns || 5,
            wordSearchFontFamily: shapeProperties.wordSearchFontFamily || 'Arial',
            wordSearchFontSize: shapeProperties.wordSearchFontSize || 24,
            wordSearchPressEnterToAdvance: shapeProperties.wordSearchPressEnterToAdvance || false,
            wordSearchPaintTextEnabled: shapeProperties.wordSearchPaintTextEnabled || false,
            wordSearchPaintTextValue: shapeProperties.wordSearchPaintTextValue || '',
            wordSearchSelectedCellIndex: null,
            wordSearchLetters: Array.from(
              {
                length:
                  (shapeProperties.wordSearchRows || 5) *
                  (shapeProperties.wordSearchColumns || 5),
              },
              () => ''
            ),
            fillEnabled: false,
          };
          break;
        case 'circleSegments':
          newShape = {
            ...newShape,
            radius: 0,
            segments: shapeProperties.segments || 4,
            circleSegmentFills: Array.from(
              { length: Math.max(1, shapeProperties.segments || 4) },
              () => shapeProperties.fillColor || '#ffffff'
            ),
          };
          break;
        case 'circle':
          newShape = {
            ...newShape,
            radius: 0,
          };
          break;

        case 'semiCircle':
          newShape = {
            ...newShape,
            radius: 0,
            rotation: 180,
            arcAngle: shapeProperties.arcAngle || 180,
          };
          break;
        case 'triangle':
          newShape = {
            ...newShape,
            radius: 0,

            // Every newly drawn triangle starts with a centred peak.
            trianglePeakOffset: 0,
          };
          break;

        case 'pentagon':
        case 'hexagon':
        case 'octagon':
          newShape = {
            ...newShape,
            radius: 0,
          };
          break;
        case 'star':
          newShape = {
            ...newShape,
            numPoints: shapeProperties.numPoints || 5,
            innerRadius: 0,
            outerRadius: 0,
          };
          break;
        default:
          break;
      }
  
      const updatedPages = [...pages];
      updatedPages[activePage].shapes.push(newShape);
      setPages(updatedPages);
      setDrawing(true);
    } else if (selectedTool === 'select') {
      if (e.target === stage) {
        selectionIgnoreShapeIdRef.current = e?.passthroughShapeId || null;

        selectionStartPoint.current = pos;
        setSelectionRect({
          x: pos.x,
          y: pos.y,
          width: 0,
          height: 0,
        });
        setIsSelecting(true);
        setSelectedShapeIds([]);
      } else {
        const clickedOnTransformer =
          e.target.getParent().className === 'Transformer';
        if (!clickedOnTransformer) {
          handleShapeClick(e, e.target.id());
        }
      }
    } else if (selectedTool === 'text') {
      pushToUndoStack();
      addText(pos.x, pos.y);      
    }
  };

  const handleStageMouseMove = (e) => {
    if (e?.evt?.buttons === 4) return;
    const stage = e.target.getStage();
    const pos = stage.getPointerPosition();
    const isShift = isShiftPressed;
  
    if (drawing) {
      const updatedPages = [...pages];
      const lastShapeIndex = updatedPages[activePage].shapes.length - 1;
      const shape = updatedPages[activePage].shapes[lastShapeIndex];
  
      if (selectedTool === 'shape') {
        const startX = shape.initialX;
        const startY = shape.initialY;
        const currentX = pos.x;
        const currentY = pos.y;
  
        let x = Math.min(startX, currentX);
        let y = Math.min(startY, currentY);
        let width = Math.abs(currentX - startX);
        let height = Math.abs(currentY - startY);
  
        // Enforce minimum size if needed
        const minSize = 1;
        width = Math.max(width, minSize);
        height = Math.max(height, minSize);
  
        if (isShift) {
          const size = Math.max(width, height);
          width = size;
          height = size;
          x = startX < currentX ? startX : startX - size;
          y = startY < currentY ? startY : startY - size;
        }
  
        const newShapeData = {
          x,
          y,
        };
  
        if (
          shape.type === 'rect' ||
          shape.type === 'roundedRect' ||
          shape.type === 'grid' ||
          shape.type === 'maze' ||
          shape.type === 'wordSearch'
        ) {
          newShapeData.width = width;
          newShapeData.height = height;

          if (shape.type === 'maze') {
            newShapeData.mazeWalls = generateMazeWalls(
              width,
              height,
              shape.mazeRows || 8,
              shape.mazeColumns || 8,
              shape.mazeExits || 2,
              shape.mazeComplexity || 3
            );
          }
        } else if (
          shape.type === 'circle' ||
          shape.type === 'semiCircle' ||
          shape.type === 'circleSegments'
        ) {
          const dx = currentX - startX;
          const dy = currentY - startY;
          let radius = Math.hypot(dx, dy);

          if (isShift) {
            radius = Math.max(Math.abs(dx), Math.abs(dy));
          }

          newShapeData.radius = radius;
          newShapeData.x = startX;
          newShapeData.y = startY;
        } else if (
          ['triangle', 'pentagon', 'hexagon', 'octagon'].includes(shape.type)
        ) {
          const dx = currentX - startX;
          const dy = currentY - startY;
          let radius = Math.hypot(dx, dy);
          if (isShift) {
            radius = Math.max(Math.abs(dx), Math.abs(dy));
          }
          newShapeData.radius = radius;
          newShapeData.x = startX;
          newShapeData.y = startY;
        } else if (shape.type === 'star') {
          const dx = currentX - startX;
          const dy = currentY - startY;
          let outerRadius = Math.hypot(dx, dy);
          if (isShift) {
            outerRadius = Math.max(Math.abs(dx), Math.abs(dy));
          }
          newShapeData.outerRadius = outerRadius;
          newShapeData.innerRadius = outerRadius / 2;
          newShapeData.x = startX;
          newShapeData.y = startY;
        }
  
        updatedPages[activePage].shapes[lastShapeIndex] = {
          ...shape,
          ...newShapeData,
        };
      } else if (selectedTool === 'line') {
        const x1 = shape.points[0];
        const y1 = shape.points[1];
        let x2 = pos.x;
        let y2 = pos.y;
  
        if (isShift) {
          const dx = x2 - x1;
          const dy = y2 - y1;
          const angle = Math.atan2(dy, dx);
          const snappedAngle = Math.round(angle / (Math.PI / 4)) * (Math.PI / 4);
          const distance = Math.hypot(dx, dy);
          x2 = x1 + distance * Math.cos(snappedAngle);
          y2 = y1 + distance * Math.sin(snappedAngle);
        }
  
        const newPoints = [x1, y1, x2, y2];
        updatedPages[activePage].shapes[lastShapeIndex] = {
          ...shape,
          points: newPoints,
        };
      } else if (selectedTool === 'pencil') {
        const currentRawPoints = shape.rawPoints || shape.points || [];

        if (currentRawPoints.length >= 2) {
          const lastX = currentRawPoints[currentRawPoints.length - 2];
          const lastY = currentRawPoints[currentRawPoints.length - 1];
          const minDistance = Math.max(1.2, 3.5 / Math.max(scale || 1, 0.01));

          if (Math.hypot(pos.x - lastX, pos.y - lastY) < minDistance) {
            return;
          }
        }

        const nextRawPoints = [...currentRawPoints, pos.x, pos.y];
        const nextDisplayPoints = buildSmoothedPencilPoints(
          nextRawPoints,
          shape.tension ?? pencilProperties.tension ?? 0.5,
          scale
        );

        updatedPages[activePage].shapes[lastShapeIndex] = {
          ...shape,
          rawPoints: nextRawPoints,
          points: nextDisplayPoints,
        };
      }
  
      setPages(updatedPages);
    } else if (isSelecting && selectionRect) {
      const sx = selectionStartPoint.current.x;
      const sy = selectionStartPoint.current.y;
      const dx = pos.x - sx;
      const dy = pos.y - sy;
  
      setSelectionRect({
        x: sx,
        y: sy,
        width: dx,
        height: dy,
      });
    }
  };
  
  
  
    const handleStageMouseUp = (e) => {
      if (e?.evt?.button === 1 || e?.button === 1) return;

      if (drawing) {
        if (selectedTool === 'shape') {
          const lastShape =
            pages[activePage]?.shapes?.[pages[activePage].shapes.length - 1];

          setDrawing(false);

          if (lastShape) {
            setSelectedShapeIds([lastShape.id.toString()]);
          }

          setSelectedTool('select');
        } else {
          setDrawing(false);
        }
      }

      if (isSelecting) {
      const selBox = {
        x: Math.min(selectionRect.x, selectionRect.x + selectionRect.width),
        y: Math.min(selectionRect.y, selectionRect.y + selectionRect.height),
        width: Math.abs(selectionRect.width),
        height: Math.abs(selectionRect.height),
      };

      const stage = stageRef.current;
      const shapes = pages[activePage].shapes;
      const selectedIds = [];

      shapes.forEach((shape) => {
        if (shape.id.toString() === selectionIgnoreShapeIdRef.current) {
          return;
        }

        const shapeNode = stage.findOne(`#${shape.id}`);
        if (shapeNode) {
          const shapeBox = shapeNode.getClientRect();
          // Intersection condition
          if (
            shapeBox.x < selBox.x + selBox.width &&
            shapeBox.x + shapeBox.width > selBox.x &&
            shapeBox.y < selBox.y + selBox.height &&
            shapeBox.y + shapeBox.height > selBox.y
          ) {
            selectedIds.push(shape.id.toString());
          }
        }
      });

       // Expand selectedIds to include full groups
      const fullSelection = new Set(selectedIds);

      selectedIds.forEach((id) => {
        const shape = shapes.find((s) => s.id.toString() === id);
        if (shape?.groupId) {
          shapes
            .filter((s) => s.groupId === shape.groupId)
            .forEach((s) => fullSelection.add(s.id.toString()));
        }
      });

      setSelectedShapeIds(Array.from(fullSelection));
      setSelectionRect(null);
      setIsSelecting(false);
      selectionIgnoreShapeIdRef.current = null;
    }
  };

  // Update Shape Properties Handlers  

      const updateSelectedWordSearchCellLetter = (letter, options = {}) => {
        const { moveNext = false, append = false } = options;

        if (selectedShapeIds.length !== 1) return;

        const selectedId = selectedShapeIds[0];
        const selectedShape = pages[activePage].shapes.find(
          (shape) => shape.id.toString() === selectedId
        );

        if (
          !selectedShape ||
          selectedShape.type !== 'wordSearch' ||
          selectedShape.locked ||
          selectedShape.wordSearchSelectedCellIndex === null
        ) {
          return;
        }

        const totalCells =
          (selectedShape.wordSearchRows || 5) *
          (selectedShape.wordSearchColumns || 5);

        const currentIndex = selectedShape.wordSearchSelectedCellIndex;
        const currentValue =
          selectedShape.wordSearchLetters?.[currentIndex] ?? '';

        let nextValue = letter || '';

        if (append) {
          nextValue = `${currentValue}${letter || ''}`;
        }

        const nextIndex =
          moveNext && currentIndex < totalCells - 1
            ? currentIndex + 1
            : currentIndex;

        const updatedPages = [...pages];
        updatedPages[activePage].shapes = updatedPages[activePage].shapes.map((shape) => {
          if (shape.id.toString() !== selectedId) return shape;

          const nextLetters = [...(shape.wordSearchLetters || [])];
          nextLetters[currentIndex] = nextValue;

          return {
            ...shape,
            wordSearchLetters: nextLetters,
            wordSearchSelectedCellIndex: nextIndex,
          };
        });

        setPages(updatedPages);

        const updatedShape = updatedPages[activePage].shapes.find(
          (shape) => shape.id.toString() === selectedId
        );

        const selectedLetter =
          updatedShape?.wordSearchLetters?.[nextIndex] ?? '';

        setShapeProperties((prev) => ({
          ...prev,
          wordSearchLetter: selectedLetter,
          wordSearchSelectedCellIndex: nextIndex,
        }));
      };

      const paintWordSearchCellText = (shapeId, cellIndex, value, options = {}) => {
        const { skipUndo = false } = options;

        const selectedShape = pages[activePage].shapes.find(
          (shape) => shape.id.toString() === shapeId.toString()
        );

        if (
          !selectedShape ||
          selectedShape.type !== 'wordSearch' ||
          selectedShape.locked
        ) {
          return;
        }

        if (!skipUndo) {
          pushToUndoStack();
        }

        const updatedPages = [...pages];
        updatedPages[activePage].shapes = updatedPages[activePage].shapes.map((shape) => {
          if (shape.id.toString() !== shapeId.toString()) return shape;

          const nextLetters = [...(shape.wordSearchLetters || [])];
          nextLetters[cellIndex] = value;

          return {
            ...shape,
            wordSearchLetters: nextLetters,
            wordSearchSelectedCellIndex: cellIndex,
          };
        });

        setPages(updatedPages);
        setSelectedShapeIds([shapeId.toString()]);

        setShapeProperties((prev) => ({
          ...prev,
          type: 'wordSearch',
          wordSearchSelectedCellIndex: cellIndex,
          wordSearchLetter: value,
        }));
      };

      const selectWordSearchCell = (shapeId, cellIndex) => {
        const updatedPages = [...pages];

        updatedPages[activePage].shapes = updatedPages[activePage].shapes.map((shape) => {
          if (shape.id.toString() !== shapeId.toString()) return shape;
          if (shape.type !== 'wordSearch') return shape;

          return {
            ...shape,
            wordSearchSelectedCellIndex: cellIndex,
          };
        });

        setPages(updatedPages);
        setSelectedShapeIds([shapeId.toString()]);
        
        const selectedShape = updatedPages[activePage].shapes.find(
          (shape) => shape.id.toString() === shapeId.toString()
        );

        const selectedLetter =
          selectedShape?.wordSearchLetters?.[cellIndex] !== undefined
            ? selectedShape.wordSearchLetters[cellIndex]
            : '';

        setShapeProperties((prev) => ({
          ...prev,
          type: 'wordSearch',
          wordSearchSelectedCellIndex: cellIndex,
          wordSearchLetter: selectedLetter,
          wordSearchRows: selectedShape?.wordSearchRows || 5,
          wordSearchColumns: selectedShape?.wordSearchColumns || 5,
          wordSearchFontFamily: selectedShape?.wordSearchFontFamily || 'Arial',
          wordSearchFontSize: selectedShape?.wordSearchFontSize || 24,
          wordSearchPressEnterToAdvance:
            selectedShape?.wordSearchPressEnterToAdvance !== undefined
              ? selectedShape.wordSearchPressEnterToAdvance
              : prev.wordSearchPressEnterToAdvance,
          wordSearchPaintTextEnabled:
            selectedShape?.wordSearchPaintTextEnabled !== undefined
              ? selectedShape.wordSearchPaintTextEnabled
              : prev.wordSearchPaintTextEnabled,
          wordSearchPaintTextValue:
            selectedShape?.wordSearchPaintTextValue !== undefined
              ? selectedShape.wordSearchPaintTextValue
              : prev.wordSearchPaintTextValue,
        }));
      };

      const clearWordSearchCellSelection = (shapeId) => {
        const updatedPages = [...pages];

        updatedPages[activePage].shapes = updatedPages[activePage].shapes.map((shape) => {
          if (shape.id.toString() !== shapeId.toString()) return shape;
          if (shape.type !== 'wordSearch') return shape;

          return {
            ...shape,
            wordSearchSelectedCellIndex: null,
          };
        });

        setPages(updatedPages);
        setSelectedShapeIds([shapeId.toString()]);

        const selectedShape = updatedPages[activePage].shapes.find(
          (shape) => shape.id.toString() === shapeId.toString()
        );

        setShapeProperties((prev) => ({
          ...prev,
          type: 'wordSearch',
          wordSearchSelectedCellIndex: null,
          wordSearchLetter: '',
          wordSearchRows: selectedShape?.wordSearchRows || 5,
          wordSearchColumns: selectedShape?.wordSearchColumns || 5,
          wordSearchFontFamily: selectedShape?.wordSearchFontFamily || 'Arial',
          wordSearchFontSize: selectedShape?.wordSearchFontSize || 24,
          wordSearchPressEnterToAdvance:
            selectedShape?.wordSearchPressEnterToAdvance !== undefined
              ? selectedShape.wordSearchPressEnterToAdvance
              : prev.wordSearchPressEnterToAdvance,
          wordSearchPaintTextEnabled:
            selectedShape?.wordSearchPaintTextEnabled !== undefined
              ? selectedShape.wordSearchPaintTextEnabled
              : prev.wordSearchPaintTextEnabled,
          wordSearchPaintTextValue:
            selectedShape?.wordSearchPaintTextValue !== undefined
              ? selectedShape.wordSearchPaintTextValue
              : prev.wordSearchPaintTextValue,
        }));
      };

      const regenerateSelectedMaze = () => {
        if (selectedShapeIds.length !== 1) return;

        const selectedId = selectedShapeIds[0];
        const selectedShape = pages[activePage].shapes.find(
          (shape) => shape.id.toString() === selectedId
        );

        if (!selectedShape || selectedShape.type !== 'maze' || selectedShape.locked) {
          return;
        }

        pushToUndoStack();

        const updatedPages = [...pages];
        updatedPages[activePage].shapes = updatedPages[activePage].shapes.map((shape) => {
          if (shape.id.toString() !== selectedId) return shape;

          return {
            ...shape,
            mazeWalls: generateMazeWalls(
              shape.width || 1,
              shape.height || 1,
              shape.mazeRows || 8,
              shape.mazeColumns || 8,
              shape.mazeExits || 2,
              shape.mazeComplexity || 3
            ),
          };
        });

        setPages(updatedPages);
      };

  const updateShapePropertiesHandler = (e, property) => {
    recordPropertyChangeForUndo('shape', property);
    let value = e.target.value;
      const validTypes = [
    'rect',
    'roundedRect',
    'circle',
    'semiCircle',
    'circleSegments',
    'polygon',
    'star',
    'triangle',
    'pentagon',
    'hexagon',
    'octagon',
    'grid',
    'maze',
    'wordSearch',
    'image',
    'line',
    'pen',
  ];
    
          if (
            property === 'dashEnabled' ||
            property === 'strokeEnabled' ||
            property === 'fillEnabled' ||
            property === 'wordSearchPressEnterToAdvance' ||
            property === 'wordSearchPaintTextEnabled'
          ) {
        value = e.target.checked;
    
        const updatedPages = [...pages];
        updatedPages[activePage].shapes = updatedPages[activePage].shapes.map((shape) => {
          if (
            selectedShapeIds.includes(shape.id.toString()) &&
            !shape.locked &&
            validTypes.includes(shape.type)
          ) {
            const updatedShape = {
              ...shape,
              [property]: value,
            };
    
            if (property === 'dashEnabled') {
              updatedShape.dash = value ? (shape.dash?.length ? shape.dash : [10, 5]) : [];
            }
    
            // ✅ Immediately update local shapeProperties too
            setShapeProperties((prev) => ({
              ...prev,
              [property]: value,
              ...(property === 'dashEnabled' && {
                dash: value ? (shape.dash?.length ? shape.dash : [10, 5]) : [],
              }),
            }));
    
            return updatedShape;
          }
          return shape;
        });
    
        setPages(updatedPages);
        return;
      }

      if (property === 'strokeStyle') {
        const style = e.target.value;

        const updatedPages = [...pages];

        updatedPages[activePage].shapes = updatedPages[activePage].shapes.map((shape) => {
          if (
            selectedShapeIds.includes(shape.id.toString()) &&
            !shape.locked &&
            validTypes.includes(shape.type)
          ) {
            const nextDash =
              style === 'dashed'
                ? [10, 5]
                : style === 'dotted'
                  ? [0, Math.max(4, (shape.strokeWidth || 2) * 2.5)]
                  : [];

            const nextDashEnabled = style === 'dashed' || style === 'dotted';

            return {
              ...shape,
              strokeStyle: style,
              dashEnabled: nextDashEnabled,
              dash: nextDash,
            };
          }

          return shape;
        });

        setShapeProperties((prev) => {
          const nextDash =
            style === 'dashed'
              ? [10, 5]
              : style === 'dotted'
                ? [0, Math.max(4, (prev.strokeWidth || 2) * 2.5)]
                : [];

          return {
            ...prev,
            strokeStyle: style,
            dashEnabled: style === 'dashed' || style === 'dotted',
            dash: nextDash,
          };
        });

        setPages(updatedPages);
        return;
      }
    
      if (property === 'dash') {
        const rawDashValue = e.target.value || '';

        value = rawDashValue
          .split(',')
          .map((val) => parseInt(val.trim(), 10))
          .filter((num) => !isNaN(num) && num > 0);

        if (value.length < 2) {
          value = [10, 5];
        }
    
        const updatedPages = [...pages];
        updatedPages[activePage].shapes = updatedPages[activePage].shapes.map((shape) => {
          if (
            selectedShapeIds.includes(shape.id.toString()) &&
            !shape.locked &&
            validTypes.includes(shape.type)
          ) {
            setShapeProperties((prev) => ({
              ...prev,
              [property]: value,
            }));

            // ALSO update the actual shape immediately
            if (selectedShapeIds.length === 1) {
              const selectedId = selectedShapeIds[0];

              const updatedPages = [...pages];
              updatedPages[activePage].shapes = updatedPages[activePage].shapes.map((shape) => {
                if (shape.id.toString() !== selectedId) return shape;

                return {
                  ...shape,
                  [property]: value,
                };
              });

              setPages(updatedPages);
            }
            return {
              ...shape,
              dash: value,
            };
          }
          return shape;
        });
    
        setPages(updatedPages);
        return;
      }
    
      if (['strokeWidth', 'opacity', 'cornerRadius', 'strokeWaveSize', 'strokeWaveFrequency', 'arcAngle', 'trianglePeakOffset', 'fillPatternSize', 'fillPatternSpacing', 'fillPatternRotation'].includes(property)) {
        value = parseFloat(value);
      } else if (['gridRows', 'gridColumns', 'segments', 'numPoints', 'mazeRows', 'mazeColumns', 'wordSearchRows', 'wordSearchColumns'].includes(property)) {
        value = Math.max(1, parseInt(e.target.value, 10) || 1);
      } else if (property === 'mazeExits') {
        value = Math.min(4, Math.max(1, parseInt(e.target.value, 10) || 1));
      } else if (property === 'mazeComplexity') {
        value = Math.min(5, Math.max(1, parseInt(e.target.value, 10) || 1));
      } else if (property === 'fillColor' || property === 'strokeColor' || property === 'wordSearchFontFamily') {
        value = e.target.value;
      }
    
    const updatedPages = [...pages];
      updatedPages[activePage].shapes = updatedPages[activePage].shapes.map((shape) => {
        if (
          selectedShapeIds.includes(shape.id.toString()) &&
          !shape.locked &&
          validTypes.includes(shape.type)
        ) {
          setShapeProperties((prev) => ({
            ...prev,
            [property]: value,
          }));

          let updatedShape = {
            ...shape,
            [property]: value,
          };

          if (updatedShape.type === 'grid') {
            const nextRows =
              property === 'gridRows'
                ? Math.max(1, parseInt(value, 10) || 1)
                : updatedShape.gridRows || 3;

            const nextColumns =
              property === 'gridColumns'
                ? Math.max(1, parseInt(value, 10) || 1)
                : updatedShape.gridColumns || 3;

            // ✅ HANDLE RESIZE ONLY
              if (property === 'gridRows' || property === 'gridColumns') {
                const oldFills = Array.isArray(updatedShape.gridCellFills)
                  ? updatedShape.gridCellFills
                  : [];

                const defaultCellColor = updatedShape.fillColor || '#ffffff';

                const resizedCellFills = Array.from(
                  { length: nextRows * nextColumns },
                  (_, index) => oldFills[index] || defaultCellColor
                );

                updatedShape = {
                  ...updatedShape,
                  gridRows: nextRows,
                  gridColumns: nextColumns,
                  gridCellFills: resizedCellFills,
                };
              }

              // ✅ HANDLE FILL TOGGLE / COLOUR (OUTSIDE resize)
              if (property === 'fillEnabled' || property === 'fillColor') {
                const rows = updatedShape.gridRows || 3;
                const columns = updatedShape.gridColumns || 3;
                const total = rows * columns;

                if (updatedShape.fillEnabled) {
                  updatedShape.gridCellFills = Array.from(
                    { length: total },
                    () => updatedShape.fillColor || '#ffffff'
                  );
                } else {
                  updatedShape.gridCellFills = Array.from(
                    { length: total },
                    () => 'transparent'
                  );
                }
              }
          }

            if (updatedShape.type === 'circleSegments') {
              const nextSegments =
                property === 'segments'
                  ? Math.max(1, parseInt(value, 10) || 1)
                  : updatedShape.segments || 4;

              if (property === 'segments') {
                const oldFills = Array.isArray(updatedShape.circleSegmentFills)
                  ? updatedShape.circleSegmentFills
                  : [];

                const defaultSegmentColor = updatedShape.fillColor || '#ffffff';

                const resizedSegmentFills = Array.from(
                  { length: nextSegments },
                  (_, index) => oldFills[index] || defaultSegmentColor
                );

                updatedShape = {
                  ...updatedShape,
                  segments: nextSegments,
                  circleSegmentFills: resizedSegmentFills,
                };
              }

              if (property === 'fillEnabled' || property === 'fillColor') {
                const total = updatedShape.segments || 4;

                if (updatedShape.fillEnabled) {
                  updatedShape.circleSegmentFills = Array.from(
                    { length: total },
                    () => updatedShape.fillColor || '#ffffff'
                  );
                } else {
                  updatedShape.circleSegmentFills = Array.from(
                    { length: total },
                    () => 'transparent'
                  );
                }
              }
            }

          if (updatedShape.type === 'maze') {
            const nextRows =
              property === 'mazeRows'
                ? Math.max(1, parseInt(value, 10) || 1)
                : updatedShape.mazeRows || 8;

            const nextColumns =
              property === 'mazeColumns'
                ? Math.max(1, parseInt(value, 10) || 1)
                : updatedShape.mazeColumns || 8;

            const nextExits =
              property === 'mazeExits'
                ? Math.min(4, Math.max(1, parseInt(value, 10) || 1))
                : updatedShape.mazeExits || 2;

            const nextComplexity =
              property === 'mazeComplexity'
                ? Math.min(5, Math.max(1, parseInt(value, 10) || 1))
                : updatedShape.mazeComplexity || 3;

            if (
              property === 'mazeRows' ||
              property === 'mazeColumns' ||
              property === 'mazeExits' ||
              property === 'mazeComplexity'
            ) {
              updatedShape = {
                ...updatedShape,
                mazeRows: nextRows,
                mazeColumns: nextColumns,
                mazeExits: nextExits,
                mazeComplexity: nextComplexity,
                mazeWalls: generateMazeWalls(
                  updatedShape.width || 1,
                  updatedShape.height || 1,
                  nextRows,
                  nextColumns,
                  nextExits,
                  nextComplexity
                ),
              };
            }
          }

          if (updatedShape.type === 'wordSearch') {
            const nextRows =
              property === 'wordSearchRows'
                ? Math.max(1, parseInt(value, 10) || 1)
                : updatedShape.wordSearchRows || 5;

            const nextColumns =
              property === 'wordSearchColumns'
                ? Math.max(1, parseInt(value, 10) || 1)
                : updatedShape.wordSearchColumns || 5;

            if (property === 'wordSearchRows' || property === 'wordSearchColumns') {
              const oldLetters = Array.isArray(updatedShape.wordSearchLetters)
                ? updatedShape.wordSearchLetters
                : [];

              const resizedLetters = Array.from(
                { length: nextRows * nextColumns },
                (_, index) => oldLetters[index] || ''
              );

              updatedShape = {
                ...updatedShape,
                wordSearchRows: nextRows,
                wordSearchColumns: nextColumns,
                wordSearchLetters: resizedLetters,
                wordSearchSelectedCellIndex:
                  updatedShape.wordSearchSelectedCellIndex !== null &&
                  updatedShape.wordSearchSelectedCellIndex < resizedLetters.length
                    ? updatedShape.wordSearchSelectedCellIndex
                    : null,
              };
            }
          }

          return updatedShape;
        }
        return shape;
      });

      setPages(updatedPages);
    };
    
  
         
  
  const updateLinePropertiesHandler = (e, property) => {
    recordPropertyChangeForUndo('line', property);
    let value = e.target.value;

    if (property === 'dashEnabled') {
      value = e.target.checked;

      setLineProperties((prev) => ({
        ...prev,
        dashEnabled: value,
        dash: value ? (prev.dash && prev.dash.length > 0 ? prev.dash : [10, 5]) : [],
      }));

      const updatedPages = [...pages];
      updatedPages[activePage].shapes = updatedPages[activePage].shapes.map((shape) => {
        if (
          shape.type === 'line' &&
          selectedShapeIds.includes(shape.id.toString()) &&
          !shape.locked
        ) {
          return {
            ...shape,
            dashEnabled: value,
            dash: value ? (shape.dash && shape.dash.length > 0 ? shape.dash : [10, 5]) : [],
          };
        }
        return shape;
      });

      setPages(updatedPages);
      return;
    }

    if (property === 'dash') {
      value = e.target.value.split(',').map((val) => {
        const parsed = parseInt(val.trim(), 10);
        return isNaN(parsed) ? 0 : parsed;
      });

      setLineProperties((prev) => ({ ...prev, dash: value }));

      const updatedPages = [...pages];
      updatedPages[activePage].shapes = updatedPages[activePage].shapes.map((shape) => {
        if (
          shape.type === 'line' &&
          selectedShapeIds.includes(shape.id.toString()) &&
          !shape.locked
        ) {
          return { ...shape, dash: value };
        }
        return shape;
      });

      setPages(updatedPages);
      return;
    }

    if (
      property === 'strokeWidth' ||
      property === 'opacity' ||
      property === 'tracingPoints' ||
      property === 'tracingLength' ||
      property === 'tracingHeight'
    ) {
      value = parseFloat(value);
    }

    setLineProperties((prev) => ({ ...prev, [property]: value }));

    const updatedPages = [...pages];
    updatedPages[activePage].shapes = updatedPages[activePage].shapes.map((shape) => {
      if (
        shape.type === 'line' &&
        selectedShapeIds.includes(shape.id.toString()) &&
        !shape.locked
      ) {
        return { ...shape, [property]: value };
      }
      return shape;
    });

    setPages(updatedPages);
  };
  
  

  // In MainApp.js, replace your updatePencilPropertiesHandler function with this version:
  const updatePencilPropertiesHandler = (e, property) => {
  recordPropertyChangeForUndo('pencil', property);
  let value;
  
    if (property === 'dashEnabled') {
      value = e.target.checked;
      setPencilProperties((prev) => ({
        ...prev,
        dashEnabled: value,
        dash: value ? (prev.dash && prev.dash.length > 0 ? prev.dash : [10, 5]) : [],
      }));
  
      const updatedPages = [...pages];
      updatedPages[activePage].shapes = updatedPages[activePage].shapes.map((shape) => {
        if (
          shape.type === 'pencil' &&
          selectedShapeIds.includes(shape.id.toString()) &&
          !shape.locked
        ) {
          const updatedShape = {
            ...shape,
            [property]: value,
          };

          if (property === 'tension') {
            const sourceRawPoints = updatedShape.rawPoints || updatedShape.points || [];

            updatedShape.points = buildSmoothedPencilPoints(
              sourceRawPoints,
              value,
              scale
            );
          }

          return updatedShape;
        }

        return shape;
      });
      setPages(updatedPages);
      return;
    }
  
    if (property === 'dash') {
      const rawDashValue = e.target.value || '';

      value = rawDashValue
        .split(',')
        .map((val) => parseInt(val.trim(), 10))
        .filter((num) => !isNaN(num) && num > 0);

      if (value.length < 2) {
        value = [10, 5];
      }
  
      setPencilProperties((prev) => ({ ...prev, dash: value }));
  
      const updatedPages = [...pages];
      updatedPages[activePage].shapes = updatedPages[activePage].shapes.map((shape) => {
        if (
          shape.type === 'pencil' &&
          selectedShapeIds.includes(shape.id.toString()) &&
          !shape.locked
        ) {
          const updatedShape = {
            ...shape,
            [property]: value,
          };

          if (property === 'tension') {
            const sourceRawPoints = updatedShape.rawPoints || updatedShape.points || [];

            updatedShape.points = buildSmoothedPencilPoints(
              sourceRawPoints,
              value,
              scale
            );
          }

          return updatedShape;
        }

        return shape;
      });
      setPages(updatedPages);
      return;
    }
  
    if (['strokeWidth', 'tension', 'opacity'].includes(property)) {
      value = parseFloat(e.target.value);
    } else if (property === 'strokeColor') {
      value = e.target.value;
    }
  
    setPencilProperties((prev) => ({ ...prev, [property]: value }));
  
    const updatedPages = [...pages];
    updatedPages[activePage].shapes = updatedPages[activePage].shapes.map((shape) => {
      if (
        shape.type === 'pencil' &&
        selectedShapeIds.includes(shape.id.toString()) &&
        !shape.locked
      ) {
        const updatedShape = { ...shape, [property]: value };

        if (property === 'tension') {
          const sourceRawPoints = updatedShape.rawPoints || updatedShape.points || [];
          updatedShape.points = buildSmoothedPencilPoints(
            sourceRawPoints,
            value,
            scale
          );
        }

        return updatedShape;
      }
      return shape;
    });
  
    setPages(updatedPages);
  };
  

  
  const updateTextPropertiesHandler = (e, property) => {
  recordPropertyChangeForUndo('text', property);
  const value =
      property === 'bold' || property === 'italic' || property === 'underline'
        ? e.target.checked
        : e.target.value;
  
    setTextProperties((prev) => ({
      ...prev,
      [property]: property === 'fontSize' ? parseInt(value) : value,
    }));
  
    const updatedPages = [...pages];
    updatedPages[activePage].shapes = updatedPages[activePage].shapes.map((shape) => {
      if (
        shape.type === 'text' &&
        selectedShapeIds.includes(shape.id.toString()) &&
        !shape.locked
      ) {
        return {
          ...shape,
          [property]:
            property === 'fontSize'
              ? parseInt(value)
              : property === 'bold' || property === 'italic' || property === 'underline'
              ? e.target.checked
              : value,
        };
      }
      return shape;
    });
  
    setPages(updatedPages);
  };
  


  // Handle Image Upload
  const triggerImageUpload = () => {
    imageInputRef.current.click();
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    pushToUndoStack();

    const reader = new FileReader();

    reader.onload = (event) => {
      const imgSrc = event.target.result;
      const img = new window.Image();

      img.onload = () => {
        const fittedSize = getFittedImageSize(img.naturalWidth, img.naturalHeight, 300);

        const newImage = {
          id: Date.now(),
          type: 'image',
          src: imgSrc,
          x: 100,
          y: 100,
          width: fittedSize.width,
          height: fittedSize.height,
          rotation: 0,
          scaleX: 1,
          scaleY: 1,
          locked: false,
        };

        const updatedPages = [...pages];
        updatedPages[activePage].shapes.push(newImage);
        setPages(updatedPages);
        setSelectedShapeIds([newImage.id.toString()]);
        setSelectedTool('select');

        e.target.value = null;
      };

      img.onerror = () => {
        console.error('Failed to load uploaded image.');
        e.target.value = null;
      };

      img.src = imgSrc;
    };

    reader.readAsDataURL(file);
  };

  // Change Canvas Orientation and Mood
  const changeCanvasOrientation = (orientation) => {
    pushToUndoStack();
    localStorage.setItem('canvasOrientation', orientation); // ✅ Save user preference

    const updatedPages = pages.map((page) => {
      if (orientation === 'portrait') {
        return {
          ...page,
          orientation: 'portrait',
          width: 794,
          height: 1123,
        };
      } else {
        return {
          ...page,
          orientation: 'landscape',
          width: 1123,
          height: 794,
        };
      }
    });
    setPages(updatedPages);
  };  

  const changeMood = (mood) => {
    if (mood === 'Default' || mood === 'Clean') {
      setWorkspaceBackgroundImage(null);
      setWorkspaceBackgroundColor('#FFF6E6');
      localStorage.setItem('workspaceMood', 'Clean'); // ✅ Save Clean
    } else if (backgroundImages[mood]) {
      setWorkspaceBackgroundImage(backgroundImages[mood]);
      setWorkspaceBackgroundColor(null);
      localStorage.setItem('workspaceMood', mood);
    }
  };
  

  // Zoom Handlers
  const handleZoomIn = () => {
    setScale((prevScale) => prevScale + 0.1);
  };

  const handleZoomOut = () => {
    setScale((prevScale) =>
      prevScale - 0.1 > 0.1 ? prevScale - 0.1 : 0.1
    );
  };

  // Page Management
  const addPage = () => {
    pushToUndoStack();
    const newPage = {
      id: pages.length,
      shapes: [],
      backgroundColor: '#ffffff',
      orientation: pages[activePage].orientation,
      width: pages[activePage].orientation === 'portrait' ? 794 : 1123,
      height: pages[activePage].orientation === 'portrait' ? 1123 : 794,
    };
    const updatedPages = [...pages, newPage];
    setPages(updatedPages);
    setActivePage(updatedPages.length - 1);
  };

  const deletePage = () => {
    pushToUndoStack();
    const updatedPages = pages.filter((page) => page.id !== deletePageId);
    const renumberedPages = updatedPages.map((page, index) => ({
      ...page,
      id: index,
    }));
    setPages(renumberedPages);
    setActivePage(0);
    setShowDeleteModal(false);
  };

  const handleDeleteClick = (pageId) => {
    if (pages.length > 1) {
      setDeletePageId(pageId);
      setShowDeleteModal(true);
    } else {
      setShowLastPageModal(true);
    }
  };

  const handlePageClick = (pageIndex) => {
    setActivePage(pageIndex);
  };

  const closeModal = () => {
    setShowDeleteModal(false);
    setShowLastPageModal(false);
  };

    // Copy and Paste Handlers
    const copySelectedShapes = () => {
      if (selectedShapeIds.length === 0) return;
    
      const lockedShapes = pages[activePage].shapes.filter(
        (shape) => selectedShapeIds.includes(shape.id.toString()) && shape.locked
      );
      const unlockedShapes = pages[activePage].shapes.filter(
        (shape) => selectedShapeIds.includes(shape.id.toString()) && !shape.locked
      );
    
      if (lockedShapes.length === selectedShapeIds.length) {
        alert('Cannot copy selected shapes because they are locked.');
        return;
      }
    
      if (lockedShapes.length > 0) {
        alert('Some selected shapes are locked and could not be copied.');
      }
    
      // ✅ Preserve group structure as-is (do not reset groupId here)
      copiedShapesRef.current = unlockedShapes.map((shape) => {
        const shapeCopy = JSON.parse(JSON.stringify(shape));

        // If it's a masked group, update live transform from canvas
        if (shapeCopy.type === 'maskedGroup' && stageRef.current) {
          const node = stageRef.current.findOne(`#${shapeCopy.id}`);
          if (node) {
            shapeCopy.x = node.x();
            shapeCopy.y = node.y();
            shapeCopy.scaleX = node.scaleX();
            shapeCopy.scaleY = node.scaleY();
            shapeCopy.rotation = node.rotation();
          }
        }

        return shapeCopy;
      });

    
      setCanPaste(true);
      setLastCopySource('internal');
    };    
    

  const pasteCopiedShapes = (options = {}) => {
    const { inPlace = false } = options;

    if (copiedShapesRef.current.length === 0) return;
    pushToUndoStack();

    const groupMap = {};
    const PASTE_OFFSET = inPlace ? 0 : 15; // ✅ 0 for paste-in-place, 15 for normal paste

    const pastedShapes = copiedShapesRef.current.map((shape) => {
      const newShape = cloneShapeWithNewIds(shape, groupMap);

      // ✅ Apply offset only when not pasting in place
      if (typeof newShape.x === 'number') newShape.x += PASTE_OFFSET;
      if (typeof newShape.y === 'number') newShape.y += PASTE_OFFSET;

      return newShape;
    });

    const updatedPages = [...pages];
    updatedPages[activePage].shapes = [
      ...updatedPages[activePage].shapes,
      ...pastedShapes,
    ];
    setPages(updatedPages);
    setSelectedShapeIds(pastedShapes.map((shape) => shape.id.toString()));

    // 👇 After pasting, wait for transformer to correctly attach if masked group is involved
    const maskedGroup = pastedShapes.find((shape) => shape.type === 'maskedGroup');
    if (maskedGroup) {
      const newId = maskedGroup.id.toString();

      const waitForMaskedNode = () => {
        const stage = stageRef.current;
        if (!stage || !transformerRef.current) return;

        const maskedNode = stage.findOne(`#${newId}`);
        if (maskedNode && maskedNode.getClientRect().width > 0) {
          transformerRef.current.nodes([maskedNode]);
          transformerRef.current.getLayer().batchDraw();
        } else {
          setTimeout(waitForMaskedNode, 20);
        }
      };

      waitForMaskedNode();
    }

    setCanPaste(false);
  };



  const handlePaste = async (e, options = {}) => {
    const { inPlace = false } = options;

    const isInput =
      e?.target?.tagName === 'INPUT' ||
      e?.target?.tagName === 'TEXTAREA' ||
      e?.target?.isContentEditable;
  
    if (isInput) return;
    if (e?.preventDefault) e.preventDefault();
  
    try {
      const clipboardItems = await navigator.clipboard.read();
  
      for (const item of clipboardItems) {
        if (item.types.includes('image/png') || item.types.includes('image/jpeg')) {
          const type = item.types.includes('image/png') ? 'image/png' : 'image/jpeg';
          const blob = await item.getType(type);
  
          const reader = new FileReader();
  
          reader.onload = (event) => {
            const imgSrc = event.target.result;
  
            // ✅ Skip only if it's *exactly* the same image, and internal is available
            if (lastExternalImageRef.current === imgSrc && copiedShapesRef.current.length > 0) {
              console.log('External image already used, falling back to internal clipboard.');
              pasteCopiedShapes({ inPlace });
              return;
            }
  
            pushToUndoStack();
  
            const img = new window.Image();

            img.onload = () => {
              const fittedSize = getFittedImageSize(img.naturalWidth, img.naturalHeight, 300);

              const newImage = {
                id: Date.now(),
                type: 'image',
                src: imgSrc,
                x: 100,
                y: 100,
                width: fittedSize.width,
                height: fittedSize.height,
                rotation: 0,
                scaleX: 1,
                scaleY: 1,
                locked: false,
              };

              const updatedPages = [...pages];
              updatedPages[activePage].shapes.push(newImage);
              setPages(updatedPages);
              setSelectedShapeIds([newImage.id.toString()]);
              setSelectedTool('select');

              copiedShapesRef.current = [];
              setCanPaste(false);
              setLastCopySource('external');
              lastExternalImageRef.current = imgSrc;
            };

            img.onerror = () => {
              console.error('Failed to load pasted image.');
            };

            img.src = imgSrc;
  
            
          };
  
          reader.readAsDataURL(blob);
          return; // ✅ Success with external image
        }
      }
  
      // ❌ No external image — fallback to internal clipboard
      if (copiedShapesRef.current.length > 0) {
        pasteCopiedShapes({ inPlace });
        return;
      }
  
      alert('Nothing to paste.');
    } catch (err) {
      console.warn('Clipboard read failed:', err);
  
      if (copiedShapesRef.current.length > 0) {
        pasteCopiedShapes({ inPlace });
      } else {
        alert('Nothing to paste.');
      }
    }
  };
  
  

  // Text Editing Handlers
  const handleTextDoubleClick = (shapeId) => {
    setSelectedTool('text');
    setSelectedShapeIds([shapeId]);

    const textShape = pages[activePage].shapes.find(
      (shape) => shape.id.toString() === shapeId
    );

    if (textShape) {
      setEditingTextPosition({
        x: textShape.x,
        y: textShape.y,
        rotation: textShape.rotation || 0,
        scaleX: textShape.scaleX || 1,
        scaleY: textShape.scaleY || 1,
        fontSize: textShape.fontSize || 20,
        fontFamily: textShape.fontFamily || 'Arial',
        fill: textShape.fill || '#000000',
        bold: textShape.bold || false,
        italic: textShape.italic || false,
        underline: textShape.underline || false,
        opacity: textShape.opacity ?? 1,
        align: textShape.align || 'left',
      });

      setEditingTextId(shapeId);
      setEditingTextValue(textShape.text);
      setIsEditingText(true);
    }
  };

  const updateTextContentHandler = (newText) => {
    pushToUndoStack();
    setEditingTextValue(newText);
    const updatedPages = [...pages];
    updatedPages[activePage].shapes = updatedPages[activePage].shapes.map(
      (shape) => {
        if (
          shape.type === 'text' &&
          selectedShapeIds.includes(shape.id.toString())
        ) {
          return {
            ...shape,
            text: newText,
          };
        }
        return shape;
      }
    );
    setPages(updatedPages);
  };

  // Shape Click and Drag Handlers
  const handleShapeClick = (e, shapeId) => {
    const shape = pages[activePage].shapes.find(s => s.id.toString() === shapeId);
  
    if (selectedTool === 'select') {
      e.cancelBubble = true;
  
      // If not in edit mode and shape is locked, ignore the click
      if (shape && shape.locked && !isEditMode) {
        setSelectedShapeIds([shapeId]); // ✅ allow selection to trigger red padlock
        return;
      }

  
      if (shape && shape.locked && isEditMode) {
        setSelectedShapeIds([shapeId]);
        return;
      }
  
      // ✅ ALT + click  ➜  duplicate the whole current selection & drag the clone of the shape you clicked
      if (e.evt.altKey && shape && !shape.locked) {
        pushToUndoStack();
      
        let sourceIds;
      
        if (selectedShapeIds.includes(shapeId)) {
          // Already selected — use unlocked ones
          sourceIds = selectedShapeIds.filter((id) => {
            const s = pages[activePage].shapes.find((s) => s.id.toString() === id);
            return s && !s.locked;
          });
        } else if (shape.groupId) {
          // Clicked on a shape in a group — copy all unlocked shapes in the group
          sourceIds = pages[activePage].shapes
            .filter((s) => s.groupId === shape.groupId && !s.locked)
            .map((s) => s.id.toString());
        } else {
          // Single unlocked shape
          sourceIds = [shapeId];
        }
      
        const groupMap = {};
        const clones = sourceIds.map((sourceId) => {
          const original = pages[activePage].shapes.find((s) => s.id.toString() === sourceId);
          if (!original) return null;

          const newId = Date.now() + Math.random();

          const deepCopy = JSON.parse(JSON.stringify(original));

            // 🧠 Get updated transform from the live Konva node if available
            if (deepCopy.type === 'maskedGroup' && stageRef.current) {
              const node = stageRef.current.findOne(`#${deepCopy.id}`);
              if (node) {
                deepCopy.x = node.x();
                deepCopy.y = node.y();
                deepCopy.scaleX = node.scaleX();
                deepCopy.scaleY = node.scaleY();
                deepCopy.rotation = node.rotation();
              }
            }


            let clone;

              if (deepCopy.type === 'maskedGroup' && Array.isArray(deepCopy.children)) {
                const groupX = original.x || 0;
                const groupY = original.y || 0;
                const [childA, childB] = deepCopy.children;

                // Determine which one is the mask
                const isChildAMask = childA.type === 'rect' || childA.type === 'pen';
                const isChildBMask = childB.type === 'rect' || childB.type === 'pen';

                let mask, content;
                if (isChildAMask) {
                  mask = childA;
                  content = childB;
                } else if (isChildBMask) {
                  mask = childB;
                  content = childA;
                } else {
                  // Fallback (unexpected case)
                  [mask, content] = deepCopy.children;
                }

                const offsetX = groupX - (content.x || 0);
                const offsetY = groupY - (content.y || 0);

                const newChildren = [mask, content].map((child) => ({
                  ...child,
                  id: Date.now() + Math.random(),
                  x: (child.x || 0) + offsetX,
                  y: (child.y || 0) + offsetY,
                }));

                clone = {
                  ...deepCopy,
                  id: newId,
                  x: groupX,
                  y: groupY,
                  children: newChildren,
                };
              } else {
                clone = {
                  ...deepCopy,
                  id: newId,
                  x: original.x || 0,
                  y: original.y || 0,
                };
              }



          if (original.groupId) {
            if (!groupMap[original.groupId]) {
              groupMap[original.groupId] = newId;
            }
            clone.groupId = groupMap[original.groupId];
          } 

          return clone;
        }).filter(Boolean); // Remove nulls

      
        const updatedPages = [...pages];
        updatedPages[activePage].shapes.push(...clones);
        setPages(updatedPages);
      
        const newIds = clones.map((c) => c.id.toString());
        setSelectedShapeIds(newIds);
      
        e.target.stopDrag();
      
        const clickedCloneId = clones[sourceIds.indexOf(shapeId)].id.toString();
      
        requestAnimationFrame(() => {
        setTimeout(() => {
          const stage = e.target.getStage();
          const newNode = stage.findOne(`#${clickedCloneId}`);
          if (newNode) {
            newNode.startDrag();
            handleShapeDragStart(clickedCloneId);
          } else {
            console.warn('Could not find node with id:', clickedCloneId);
          }
        }, 10);
      });

      
        return;
      }
         

      

      // ✅ SHIFT + Click to multi-select
      if (e.evt.shiftKey) {
        const clickedShape = pages[activePage].shapes.find((s) => s.id.toString() === shapeId);
        let newSelection = [...selectedShapeIds];

        if (clickedShape?.groupId) {
          const groupShapes = pages[activePage].shapes
            .filter((s) => s.groupId === clickedShape.groupId && !s.locked)
            .map((s) => s.id.toString());

          const allGroupSelected = groupShapes.every((id) => newSelection.includes(id));

          if (allGroupSelected) {
            // Deselect the group
            newSelection = newSelection.filter((id) => !groupShapes.includes(id));
          } else {
            // Add group shapes
            groupShapes.forEach((id) => {
              if (!newSelection.includes(id)) newSelection.push(id);
            });
          }
        } else {
          // Not in a group: toggle single shape
          const index = newSelection.indexOf(shapeId);
          if (index >= 0) {
            newSelection.splice(index, 1);
          } else {
            newSelection.push(shapeId);
          }
        }

        setSelectedShapeIds(newSelection);
      } else {
        if (!selectedShapeIds.includes(shapeId)) {
          const clickedShape = pages[activePage].shapes.find((s) => s.id.toString() === shapeId);
          if (clickedShape?.groupId) {
            const groupShapes = pages[activePage].shapes
              .filter((s) => s.groupId === clickedShape.groupId && !s.locked)
              .map((s) => s.id.toString());
            setSelectedShapeIds(groupShapes);
          } else {
            setSelectedShapeIds([shapeId]);
          }
        }
      }

    }
  };
  
  
  
  const handleShapeDragStart = (shapeId) => {
    if (selectedShapeIds.includes(shapeId)) {
      if (isMovingRef.current) return;
    
      isMovingRef.current = true;
    
      // ✅ Push only once for group move
      pushToUndoStack();
    
      // Store initial positions
      const initialPositions = {};
      pages[activePage].shapes.forEach((shape) => {
        if (selectedShapeIds.includes(shape.id.toString())) {
          initialPositions[shape.id.toString()] = { x: shape.x, y: shape.y };
        }
      });
      initialPositionsRef.current = initialPositions;
    
      // Store initial pointer position
      const stage = stageRef.current;
      const pointerPos = stage.getPointerPosition();
      initialPointerPosRef.current = { x: pointerPos.x, y: pointerPos.y };
    }
    
  };

  const handleShapeDragEnd = ({ target }) => {
    const shapeId = target.id;
    const newX = target.x;
    const newY = target.y;

    if (isMovingRef.current) {
      isMovingRef.current = false;
    }

    setPages((prevPages) => {
      const newPages = [...prevPages];
      newPages[activePage].shapes = newPages[activePage].shapes.map((shape) => {
        if (shape.id.toString() === shapeId) {
          // ✅ Update masked group position at top-level
          if (shape.type === 'maskedGroup') {
            return {
              ...shape,
              x: newX,
              y: newY,
            };
          }

          // ✅ For normal shapes
          return {
            ...shape,
            x: newX,
            y: newY,
          };
        }
        return shape;
      });
      return newPages;
    });
  };


  
  // Shape Transform End Handler
  const handleTransformEnd = (shapeId, updatedProps) => {
    const updatedPages = [...pages];
    const page = updatedPages[activePage];

    page.shapes = page.shapes.map((shape) => {
      if (shape.id.toString() === shapeId.toString()) {
        // Special handling for maskedGroup: apply transform to group AND children (if needed)
        if (shape.type === 'maskedGroup') {
          return {
            ...shape,
            ...updatedProps, // apply rotation/scale/x/y to the group itself
          };
        }

        // All other shapes
        return {
          ...shape,
          ...updatedProps,
        };
      }
      return shape;
    });

    setPages(updatedPages);
  };


  // Save Canvas
  const saveStateToLocalStorage = () => {
    setIsLoading(true);  // Show the loader when saving
    const stateToSave = {
      pages,
      activePage,
      // Add any other state variables you want to save
    };
    localStorage.setItem('savedCanvasState', JSON.stringify(stateToSave));
    setIsLoading(false); // Hide the loader after saving
    alert('Your work has been saved.');
  };
  

  useEffect(() => {
    if (!initialState) {
      const savedState = localStorage.getItem('savedCanvasState');
      if (savedState) {
        const parsedState = JSON.parse(savedState);
        setPages(parsedState.pages);
        setActivePage(parsedState.activePage);
        // Set other state variables if needed
      }
    }
  }, [initialState]);

  // Save Canvas as Image
  const saveCanvas = async () => {
    try {
      setIsExporting(true);

      // Allow React/Konva to redraw without visual-only tools.
      await new Promise((resolve) => {
        requestAnimationFrame(() => {
          requestAnimationFrame(resolve);
        });
      });

      const stage = stageRef.current;

      if (!stage) {
        throw new Error('Canvas stage is not ready.');
      }

      stage.batchDraw();

      const uri = stage.toDataURL({ pixelRatio: 3 });
      const link = document.createElement('a');

      link.download = sanitizeFilename(exportFilename || 'canvas', 'png');
      link.href = uri;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('PNG export failed:', error);
      alert('The image could not be exported.');
    } finally {
      setIsExporting(false);
    }
  };

  const sanitizeFilename = (name, extension) => {
    // Remove invalid characters
    const sanitized = name.replace(/[^a-z0-9_-]/gi, '_');
    // Ensure the filename is not empty
    return sanitized ? `${sanitized}.${extension}` : `canvas.${extension}`;
  };
  
    // Determine Selection Types
  const selectedTextShapes = currentPage.shapes.filter(
    (shape) =>
      shape.type === 'text' && selectedShapeIds.includes(shape.id.toString())
  );

  const unlockedSelectedShapes = currentPage.shapes.filter(
    (shape) =>
      selectedShapeIds.includes(shape.id.toString()) && !shape.locked
  );
  
  const allSelectedAreLines = unlockedSelectedShapes.length > 0 &&
    unlockedSelectedShapes.every((shape) => shape.type === 'line');
  
  const allSelectedAreShapes = unlockedSelectedShapes.length > 0 &&
    unlockedSelectedShapes.every((shape) =>
      ['rect', 'roundedRect', 'circle', 'semiCircle', 'triangle', 'pentagon', 'hexagon', 'octagon', 'star', 'grid', 'maze', 'pen', 'circleSegments', 'wordSearch'].includes(shape.type)
    );
  
  const allSelectedAreText = unlockedSelectedShapes.length > 0 &&
    unlockedSelectedShapes.every((shape) => shape.type === 'text');
  
  const allSelectedArePencilLines = unlockedSelectedShapes.length > 0 &&
    unlockedSelectedShapes.every((shape) => shape.type === 'pencil');
  
  const allSelectedAreImages = unlockedSelectedShapes.length > 0 &&
    unlockedSelectedShapes.every((shape) => shape.type === 'image');
  
  const isTracingLine = unlockedSelectedShapes.length === 1 &&
    unlockedSelectedShapes[0]?.meta?.tracingType;
  
  const allSelectedArePenShapes =
  selectedShapeIds.length > 0 &&
  selectedShapeIds.every((id) => {
    const shape = pages[activePage].shapes.find((s) => s.id.toString() === id);
    return shape && shape.type === 'pen';
  });
  
const isGroupSelection =
  selectedShapeIds.length >= 2 &&
  (() => {
    const groupIds = new Set();
    const shapes = pages[activePage].shapes;
    for (let id of selectedShapeIds) {
      const shape = shapes.find((s) => s.id.toString() === id);
      if (!shape?.groupId) return false; // Not grouped
      groupIds.add(shape.groupId);
    }
    return groupIds.size === 1;
  })();


// Text properties input passing
useEffect(() => {
  if (!allSelectedAreText || selectedShapeIds.length !== 1) return;

  const selectedTextShape = pages[activePage].shapes.find(
    (shape) =>
      shape.type === 'text' &&
      shape.id.toString() === selectedShapeIds[0]
  );

  if (!selectedTextShape) return;

  setTextProperties((prev) => {
    const next = {
      text: selectedTextShape.text,
      fontFamily: selectedTextShape.fontFamily,
      fontSize: selectedTextShape.fontSize,
      fill: selectedTextShape.fill,
      opacity: selectedTextShape.opacity,
      bold: selectedTextShape.bold,
      italic: selectedTextShape.italic,
      underline: selectedTextShape.underline,
    };

    if (
      prev.text === next.text &&
      prev.fontFamily === next.fontFamily &&
      prev.fontSize === next.fontSize &&
      prev.fill === next.fill &&
      prev.opacity === next.opacity &&
      prev.bold === next.bold &&
      prev.italic === next.italic &&
      prev.underline === next.underline
    ) {
      return prev;
    }

    return next;
  });
}, [selectedShapeIds, allSelectedAreText, pages, activePage]);

// Shape properties input passing
useEffect(() => {
  if (selectedShapeIds.length === 1) {
    const selectedShape = pages[activePage].shapes.find(
      (shape) => shape.id.toString() === selectedShapeIds[0]
    );

    // ✅ Skip locked shapes from being loaded into the sidebar
    if (!selectedShape || selectedShape.locked) return;

       switch (selectedShape.type) {
      case 'rect':
      case 'roundedRect':
      case 'circle':
      case 'semiCircle':
      case 'circleSegments':
      case 'polygon':
      case 'star':
      case 'triangle':
      case 'pentagon':
      case 'hexagon':
      case 'octagon':
      case 'grid':
      case 'maze':
      case 'wordSearch':  
        setShapeProperties({
          type: selectedShape.type,
          strokeWidth: selectedShape.strokeWidth || 2,
          strokeColor: selectedShape.strokeColor || '#000000',
          fillColor: selectedShape.fillColor || '#FFFFFF',
          fillStyle: selectedShape.fillStyle || 'solid',
          fillSecondaryColor: selectedShape.fillSecondaryColor || '#F2D6A7',
          fillPatternSize: selectedShape.fillPatternSize || 10,
          fillPatternSpacing: selectedShape.fillPatternSpacing ?? 8,
          fillPatternRotation: selectedShape.fillPatternRotation || 0,
          opacity: selectedShape.opacity || 1,
          cornerRadius: selectedShape.cornerRadius || 10,
          dashEnabled: selectedShape.dashEnabled || false,
          dash: selectedShape.dash || [10, 5],
          strokeStyle: selectedShape.strokeStyle || 'solid',
          strokeWaveSize: selectedShape.strokeWaveSize || 8,
          strokeWaveFrequency: selectedShape.strokeWaveFrequency || 16,
          strokeEnabled:
            selectedShape.strokeEnabled !== undefined
              ? selectedShape.strokeEnabled
              : true,
          fillEnabled:
            selectedShape.fillEnabled !== undefined
              ? selectedShape.fillEnabled
              : true,
          gridRows: selectedShape.gridRows || 3,
          gridColumns: selectedShape.gridColumns || 3,
          segments: selectedShape.segments || 4,
          arcAngle: selectedShape.arcAngle || 180,
          numPoints: selectedShape.numPoints || 5,
          trianglePeakOffset: selectedShape.trianglePeakOffset || 0,
          mazeRows: selectedShape.mazeRows || 8,
          mazeColumns: selectedShape.mazeColumns || 8,
          mazeExits: selectedShape.mazeExits || 2,
          mazeComplexity: selectedShape.mazeComplexity || 3,
          wordSearchRows: selectedShape.wordSearchRows || 5,
          wordSearchColumns: selectedShape.wordSearchColumns || 5,
          wordSearchFontFamily: selectedShape.wordSearchFontFamily || 'Arial',
          wordSearchFontSize: selectedShape.wordSearchFontSize || 24,
          wordSearchPressEnterToAdvance:
            selectedShape.wordSearchPressEnterToAdvance !== undefined
              ? selectedShape.wordSearchPressEnterToAdvance
              : false,
          wordSearchPaintTextEnabled:
            selectedShape.wordSearchPaintTextEnabled !== undefined
              ? selectedShape.wordSearchPaintTextEnabled
              : false,
          wordSearchPaintTextValue:
            selectedShape.wordSearchPaintTextValue !== undefined
              ? selectedShape.wordSearchPaintTextValue
              : '',
          wordSearchSelectedCellIndex: selectedShape.wordSearchSelectedCellIndex ?? null,
          wordSearchLetter:
            selectedShape.wordSearchSelectedCellIndex !== null &&
            selectedShape.wordSearchLetters?.[selectedShape.wordSearchSelectedCellIndex] !== undefined
              ? selectedShape.wordSearchLetters[selectedShape.wordSearchSelectedCellIndex]
              : '',
        });
        break;        
      case 'line':
        setLineProperties({
          strokeWidth: selectedShape.strokeWidth || 2,
          strokeColor: selectedShape.strokeColor || '#000000',
          opacity: selectedShape.opacity ?? 1,
          dashEnabled: selectedShape.dashEnabled || false,
          dash: selectedShape.dash || [10, 5],
        });
        break;
        case 'pen': {
          const penProps = {
            strokeWidth: selectedShape.strokeWidth || 2,
            strokeColor: selectedShape.strokeColor || '#000000',
            fillColor:   selectedShape.fillColor   || '#FFFFFF',
            fillStyle: selectedShape.fillStyle || 'solid',
            fillSecondaryColor: selectedShape.fillSecondaryColor || '#F2D6A7',
            fillPatternSize: selectedShape.fillPatternSize || 10,
            fillPatternSpacing: selectedShape.fillPatternSpacing ?? 8,
            fillPatternRotation: selectedShape.fillPatternRotation || 0,
            opacity:     selectedShape.opacity     ?? 1,
            dashEnabled: selectedShape.dashEnabled || false,
            dash:        selectedShape.dash        || [10, 5],
            fillEnabled: selectedShape.fillEnabled ?? true,
            strokeEnabled: selectedShape.strokeEnabled ?? true,
            tension: penToolSettings.tension || 0.5,

          };
          setPenProperties(penProps);   // for pen-specific handlers
          setShapeProperties(penProps); // for shared visual panel display
          break;
        }
 
      case 'pencil':
        setPencilProperties({
          strokeWidth: selectedShape.strokeWidth || 2,
          strokeColor: selectedShape.strokeColor || '#000000',
          tension: selectedShape.tension || 0.5,
          dashEnabled: selectedShape.dashEnabled || false,
          dash: selectedShape.dash || [10, 5],
          opacity: selectedShape.opacity ?? 1,
        });
        break;
      case 'text':
        setTextProperties({
          text: selectedShape.text || '',
          fontFamily: selectedShape.fontFamily || 'Arial',
          fontSize: selectedShape.fontSize || 20,
          fill: selectedShape.fill || '#000000',
          opacity: selectedShape.opacity || 1,
          bold: selectedShape.bold || false,
          italic: selectedShape.italic || false,
          underline: selectedShape.underline || false,
        });
        break;        
    
      case 'image':
        setShapeProperties((prevProperties) => ({
          ...prevProperties,
          opacity: selectedShape.opacity || 1,
        }));
        break;
      default:
        break;
    }
  }
}, [selectedShapeIds, pages, activePage]);



 // Add useEffect to log grid visibility changes
 useEffect(() => {
  console.log('isGridVisible:', isGridVisible);
}, [isGridVisible]);

useEffect(() => {
  console.log('isQuartersGridVisible:', isQuartersGridVisible);
}, [isQuartersGridVisible]);

useEffect(() => {
  console.log('isThirdsGridVisible:', isThirdsGridVisible);
}, [isThirdsGridVisible]);

  // Handle Paste from Clipboard and Internal Paste
  const handleContextMenuPaste = async () => {
    const fakeEvent = {
      preventDefault: () => {},
      target: { tagName: '', isContentEditable: false }
    };
    await handlePaste(fakeEvent);
    hideContextMenu();
  };
  
  const handleContextMenuPasteInPlace = async () => {
    const fakeEvent = {
      preventDefault: () => {},
      target: { tagName: '', isContentEditable: false }
    };

    await handlePaste(fakeEvent, { inPlace: true });
    hideContextMenu();
  };

  

  // Handle Context Menu
  const handleContextMenu = (e) => {
    e.preventDefault();

    const x = e.clientX;
    const y = e.clientY;

    setContextMenuPosition({ x, y });
    setContextMenuVisible(true);

    requestAnimationFrame(() => {
      const menu = contextMenuRef.current;
      if (!menu) return;

      const rect = menu.getBoundingClientRect();

      let newX = x;
      let newY = y;

      if (rect.right > window.innerWidth) {
        newX = x - rect.width;
      }

      if (rect.bottom > window.innerHeight) {
        newY = y - rect.height;
      }

      setContextMenuPosition({
        x: newX,
        y: newY,
      });
    });
  };

  const hideContextMenu = () => {
    setContextMenuVisible(false);
  };

  // Add event listeners for paste and context menu
  useEffect(() => {    
    window.addEventListener('paste', handlePaste);
    window.addEventListener('contextmenu', handleContextMenu);
    window.addEventListener('click', hideContextMenu);

    return () => {
      window.removeEventListener('paste', handlePaste);
      window.removeEventListener('contextmenu', handleContextMenu);
      window.removeEventListener('click', hideContextMenu);
    };
  }, [pages, activePage, canPaste]);

  // Undo and Redo Stack Size Logger (Optional)
  useEffect(() => {
    console.log('Undo Stack Size:', undoStack.current.length);
    console.log('Redo Stack Size:', redoStack.current.length);
  }, [undoStack.current.length, redoStack.current.length]);

  const handleUploadTemplate = async (data) => {
    const formData = new FormData();
    formData.append('json', JSON.stringify({
      ...data,
      user: currentUser,
    }));
  
    try {
      const res = await fetch('https://edugroove-backend.onrender.com/api/upload-template', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('edugroove_access_token')}`,
        },
        body: formData,
      });
  
      const text = await res.text();
      let result;
  
      try {
        result = JSON.parse(text);
      } catch (e) {
        console.error('Non-JSON response:', text);
        alert(
          `Upload failed (${res.status || 'server error'}). ` +
          'The server returned an invalid response. Please check the Render logs.'
        );
        return;
      }
  
      if (res.ok) {
        setTemplates(prev => [...prev, data]);
        setEditingTemplate(data);

        alert(result.message || '✅ Template uploaded!');
        setShowUploadModal(false);
      } else {
        alert(
          '❌ Upload failed: ' +
          (result.error || result.message || `Server returned ${res.status}`)
        );
      }
    } catch (error) {
      alert('Server error uploading template.');
      console.error('Upload failed:', error);
    }
  };  
  
  const handleTemplateOpen = (templateData) => {
  setPages(templateData.pages || []);
  setActivePage(0);
  setEditingTemplate(templateData); // ✅ This sets metadata for UploadTemplateModal
  setShowUploadModal(true);
};

  
  const [categories, setCategories] = useState([]);

    useEffect(() => {
    const loadData = async () => {
      try {
        const res = await fetch(
          `https://edugroove.com/edugroove_backend/get_categories.php?v=${Date.now()}`,
          {
            cache: 'no-store',
          }
        );
        const categoryData = await res.json();

        if (!Array.isArray(categoryData)) return;

        setCategories(categoryData);

        const allTemplates = [];

        for (const category of categoryData) {
          try {
            const indexUrl =
              `https://edugroove.com/edugroove_backend/templates/${category}/index.json?v=${Date.now()}`;

            const indexRes = await fetch(indexUrl, {
              cache: 'no-store',
            });

            if (!indexRes.ok) continue;

            const indexData = await indexRes.json();

            if (!Array.isArray(indexData)) continue;

            indexData.forEach(t => {
              allTemplates.push({
                ...t,
                category: t.category || category,
              });
            });

          } catch (err) {
            console.warn('Failed loading category:', category);
          }
        }

        setTemplates(allTemplates);

      } catch (err) {
        console.error('Failed to load data', err);
      }
    };

    loadData();
  }, []);

  const applyPenMask = () => {
    if (selectedShapeIds.length !== 2) return;

    pushToUndoStack();

    const updatedPages = [...pages]; // fresh copy
    const shapes = updatedPages[activePage].shapes;

    // Refetch actual current versions of the selected shapes
    const [id1, id2] = selectedShapeIds;
    const freshShape1 = shapes.find((s) => s.id.toString() === id1);
    const freshShape2 = shapes.find((s) => s.id.toString() === id2);

    if (!freshShape1 || !freshShape2) return;

    let maskShape, contentShape;
    const validMaskTypes = ['pen', 'rect'];

    if (validMaskTypes.includes(freshShape1.type)) {
      maskShape = freshShape1;
      contentShape = freshShape2;
    } else if (validMaskTypes.includes(freshShape2.type)) {
      maskShape = freshShape2;
      contentShape = freshShape1;
    } else {
      return;
    }

    // Position group at contentShape, make children relative
    const groupX = contentShape.x || 0;
    const groupY = contentShape.y || 0;

    const newGroup = {
      id: Date.now(),
      type: 'maskedGroup',
      groupId: `mask-${Date.now()}`,
      x: groupX,
      y: groupY,
      maskId: maskShape.id,
      children: [maskShape, contentShape].map((child) => ({
        ...JSON.parse(JSON.stringify(child)),
        id: Date.now() + Math.random(),
        x: (child.x || 0) + (groupX - (contentShape.x || 0)),
        y: (child.y || 0) + (groupY - (contentShape.y || 0)),
      })),
    };



    // Remove originals
    updatedPages[activePage].shapes = shapes.filter(
      (s) => !selectedShapeIds.includes(s.id.toString())
    );

    // Add new masked group
    updatedPages[activePage].shapes.push(newGroup);

    // Save
    setPages(updatedPages);
    setSelectedShapeIds([String(newGroup.id)]);

    const waitForMaskedNode = () => {
      const stage = stageRef.current;
      if (!stage || !transformerRef.current) return;

      const maskedNode = stage.findOne(`#${newGroup.id}`);
      if (maskedNode && maskedNode.getClientRect().width > 0) {
        transformerRef.current.nodes([maskedNode]);
        transformerRef.current.getLayer().batchDraw();
      } else {
        setTimeout(waitForMaskedNode, 20); // Keep checking every 20ms until it's ready
      }
    };

    waitForMaskedNode();
  };


  const releasePenMask = () => {
    const updatedPages = [...pages];
    const shapes = updatedPages[activePage].shapes;

    const newShapes = [];

    for (const shape of shapes) {
      if (
        shape.type === 'maskedGroup' &&
        selectedShapeIds.includes(shape.id.toString())
      ) {
        const groupX = shape.x || 0;
        const groupY = shape.y || 0;

        const [maskShape, contentShape] = shape.children;

        const offsetX = groupX - contentShape.x;
          const offsetY = groupY - contentShape.y;

          const restoreChild = (child) => {
            const baseX = typeof child.x === 'number' ? child.x : 0;
            const baseY = typeof child.y === 'number' ? child.y : 0;

            return {
              ...child,
              x: baseX + offsetX,
              y: baseY + offsetY,
            };
          };

          const restoredMask = restoreChild(maskShape);
          const restoredContent = restoreChild(contentShape);

        newShapes.push(restoredContent, restoredMask);
      } else {
        newShapes.push(shape);
      }
    }

    updatedPages[activePage].shapes = newShapes;
    setPages(updatedPages);
    setSelectedShapeIds([]);
  };

  
  // Render method
  if (!isAppReady) {
    return (
      <div className="main-app">
        {isLoading && <LottieLoader message={loaderMessage || "Loading Groovesheets"} />}
      </div>
    );
  }
  
  return (
    <div
      className="main-app"
      style={{
        backgroundColor: workspaceBackgroundColor,
        backgroundImage: workspaceBackgroundImage ? `url(${workspaceBackgroundImage})` : 'none',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        transition: 'background-image 0.5s ease-in-out, background-color 0.5s ease-in-out',
      }}
    >
      

      <TopMenuBar
        undo={undo}
        redo={redo}
        deleteSelectedShapes={deleteSelectedShapes}
        selectedShapeIds={selectedShapeIds}
        copySelectedShapes={copySelectedShapes}
        pasteCopiedShapes={pasteCopiedShapes}
        canPaste={canPaste}
        bringForward={bringForward}
        sendBack={sendBack}
        isEditMode={isEditMode}
        toggleEditMode={toggleEditMode}
        areAllSelectedLocked={areAllSelectedLocked}
        lockSelectedShapes={lockSelectedShapes}
        copyToAllPages={copyToAllPages}
        onOpenToolsTour={openToolsTour}
      />

    
  
      
      <Sidebar
        selectedTool={selectedTool}
        handleToolClick={handleToolClick}
        triggerImageUpload={triggerImageUpload}
        hoveredTool={hoveredTool}
        handleMouseEnter={handleMouseEnter}
        handleMouseLeave={handleMouseLeave}
        isSidebarCollapsed={isSidebarCollapsed}
        toggleSidebar={toggleSidebar}
        imageInputRef={imageInputRef}
        handleImageUpload={handleImageUpload}
      />
    
  
      <div
        className="workspace"
        data-tour="editor-workspace"
        style={{
          backgroundColor: workspaceBackgroundColor,
          backgroundImage: workspaceBackgroundImage ? `url(${workspaceBackgroundImage})` : 'none',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          transition: 'background-image 0.5s ease-in-out, background-color 0.5s ease-in-out',
          cursor: isMiddleGrabbing ? 'grabbing' : 'default',
        }}
        onMouseDown={handleCanvasMouseDownDrag}
        onMouseMove={handleCanvasMouseMoveDrag}
        onMouseUp={handleCanvasMouseUpDrag}
      >
        <CanvasArea
          pages={pages}
          activePage={activePage}
          setPages={setPages}
          selectedTool={selectedTool}
          setSelectedTool={setSelectedTool}
          selectedShapeIds={selectedShapeIds}
          setSelectedShapeIds={setSelectedShapeIds}
          selectionRect={selectionRect}
          setSelectionRect={setSelectionRect}
          isSelecting={isSelecting}
          setIsSelecting={setIsSelecting}
          stageRef={stageRef}
          transformerRef={transformerRef}
          panOffset={panOffset}
          scale={scale}
          drawing={drawing}
          setDrawing={setDrawing}
          handleStageMouseDown={handleStageMouseDown}
          handleStageMouseMove={handleStageMouseMove}
          handleStageMouseUp={handleStageMouseUp}
          isShiftPressed={isShiftPressed}
          isEditingText={isEditingText}
          editingTextPosition={editingTextPosition}
          editingTextId={editingTextId}
          editingTextValue={editingTextValue}
          updateTextContentHandler={updateTextContentHandler}
          setIsEditingText={setIsEditingText}
          textProperties={textProperties}
          handleShapeClick={handleShapeClick}
          handleTextDoubleClick={handleTextDoubleClick}
          handleShapeDragStart={handleShapeDragStart}
          handleShapeDragEnd={handleShapeDragEnd}
          handleTransformEnd={handleTransformEnd}
          isGridVisible={isGridVisible}
          setIsGridVisible={setIsGridVisible}
          isQuartersGridVisible={isQuartersGridVisible}
          setIsQuartersGridVisible={setIsQuartersGridVisible}
          isThirdsGridVisible={isThirdsGridVisible}
          setIsThirdsGridVisible={setIsThirdsGridVisible}
          isRulerGridVisible={isRulerGridVisible}
          setIsRulerGridVisible={setIsRulerGridVisible}

          isMeasurementRulerVisible={isMeasurementRulerVisible}
          isMeasurementRulerSelected={isMeasurementRulerSelected}
          setIsMeasurementRulerSelected={setIsMeasurementRulerSelected}
          measurementRulerSettings={measurementRulerSettings}
          setMeasurementRulerSettings={setMeasurementRulerSettings}

          isCustomGridVisible={isCustomGridVisible}
          customGridSettings={customGridSettings}
          isEditMode={isEditMode}
          pushToUndoStack={pushToUndoStack}
          isExporting={isExporting}
          penPoints={penPoints}
          setPenPoints={setPenPoints}
          penTempLineId={penTempLineId}
          setPenTempLineId={setPenTempLineId}
          penMousePos={penMousePos}
          setPenMousePos={setPenMousePos}
          penToolSettings={penToolSettings}
          setPenToolSettings={setPenToolSettings}  
          logoUrl={logoUrl}
          squareLogoUrl={squareLogoUrl} 
          canvasBursts={canvasBursts}
          setCanvasBursts={setCanvasBursts}  
          isMiddleGrabbing={isMiddleGrabbing}   
          selectWordSearchCell={selectWordSearchCell}
          clearWordSearchCellSelection={clearWordSearchCellSelection}           
          bucketSettings={bucketSettings}
          applyBucketToShape={applyBucketToShape}
          applyBucketToGridCell={applyBucketToGridCell}
          applyBucketToCircleSegment={applyBucketToCircleSegment}
          paintWordSearchCellText={paintWordSearchCellText}
        />
      </div>

      
      <ToolPropertiesPanel
        isPropertiesCollapsed={isPropertiesCollapsed}
        togglePropertiesPanel={togglePropertiesPanel}
        selectedShapeIds={selectedShapeIds}
        allSelectedAreText={allSelectedAreText}
        allSelectedAreShapes={allSelectedAreShapes}
        allSelectedAreImages={allSelectedAreImages}
        shapeProperties={shapeProperties}
        updateShapePropertiesHandler={updateShapePropertiesHandler}
        textProperties={textProperties}
        updateTextPropertiesHandler={updateTextPropertiesHandler}
        selectedTool={selectedTool}
        currentTemplate={editingTemplate}
        setSelectedShapeType={setSelectedShapeType}
        selectedShapeType={selectedShapeType}
        changeCanvasOrientation={changeCanvasOrientation}
        pages={pages}
        activePage={activePage}
        changeMood={changeMood}
        handleZoomIn={handleZoomIn}
        handleZoomOut={handleZoomOut}
        updateLinePropertiesHandler={updateLinePropertiesHandler}
        lineProperties={lineProperties}
        updatePencilPropertiesHandler={updatePencilPropertiesHandler}
        pencilProperties={pencilProperties}
        addBlankPage={addBlankPage}
        duplicateCurrentPage={duplicateCurrentPage}
        saveState={saveStateToLocalStorage}
        exportToPDF={exportToPDF}
        openAdvancedPDFExport={openAdvancedPDFExport}
        triggerFileInput={triggerFileInput}
        triggerPDFInput={triggerPDFInput}
        triggerImageUpload={triggerImageUpload}
        saveStateAsJSON={saveStateAsJSON}
        isGridVisible={isGridVisible}
        setIsGridVisible={setIsGridVisible}
        isQuartersGridVisible={isQuartersGridVisible}
        setIsQuartersGridVisible={setIsQuartersGridVisible}
        isThirdsGridVisible={isThirdsGridVisible}
        setIsThirdsGridVisible={setIsThirdsGridVisible}
        isRulerGridVisible={isRulerGridVisible}
        setIsRulerGridVisible={setIsRulerGridVisible}

        isCustomGridVisible={isCustomGridVisible}
        setIsCustomGridVisible={setIsCustomGridVisible}
        customGridSettings={customGridSettings}
        setCustomGridSettings={setCustomGridSettings}

        isMeasurementRulerVisible={isMeasurementRulerVisible}
        setIsMeasurementRulerVisible={setIsMeasurementRulerVisible}
        isMeasurementRulerSelected={isMeasurementRulerSelected}
        setIsMeasurementRulerSelected={setIsMeasurementRulerSelected}
        measurementRulerSettings={measurementRulerSettings}
        setMeasurementRulerSettings={setMeasurementRulerSettings}
        finalizePropertyChange={pushToUndoStack}
        exportFilename={exportFilename}
        setExportFilename={setExportFilename}
        allSelectedAreLines={allSelectedAreLines}
        allSelectedArePencilLines={allSelectedArePencilLines}
        setShapeProperties={setShapeProperties}
        setShowImageLibraryModal={setShowImageLibraryModal}
        onSetSquareLogo={setSquareLogoPlaceholder}
        onSetLogo={setLogoPlaceholder}
        onSwapImage={openSwapImageModal}
        onUploadTemplateClick={() => {
          setEditingTemplate((prev) => (prev ? { ...prev } : null)); // 👈 re-triggers modal fill
          setShowUploadModal(true);
        }}
        setPages={setPages}
        setSelectedShapeIds={setSelectedShapeIds}
        setSelectedTool={setSelectedTool}
        alignSelectionVertically={alignSelectionVertically}
        alignSelectionHorizontally={alignSelectionHorizontally}
        distributeSelectionHorizontally={distributeSelectionHorizontally}
        distributeSelectionVertically={distributeSelectionVertically}
        groupSelectedShapes={groupSelectedShapes}
        ungroupSelectedShapes={ungroupSelectedShapes}
        flipSelectedHorizontally={flipSelectedHorizontally}
        flipSelectedVertically={flipSelectedVertically}
        penToolSettings={penToolSettings}
        setPenToolSettings={setPenToolSettings}
        allSelectedArePenShapes={allSelectedArePenShapes}
        isGroupSelection={isGroupSelection}
        onApplyPenMask={applyPenMask}
        onReleasePenMask={releasePenMask}
        pushToUndoStack={pushToUndoStack}
        recordPropertyChangeForUndo={recordPropertyChangeForUndo}
        regenerateSelectedMaze={regenerateSelectedMaze}        
        updateSelectedWordSearchCellLetter={updateSelectedWordSearchCellLetter}
        bucketSettings={bucketSettings}
        setBucketSettings={setBucketSettings}
      />
      
  
      {isLoading && <LottieLoader message={loaderMessage || "Loading..."} />}

      {showAdvancedPDFExportModal && (
        <AdvancedPDFExportModal
          pages={pages}
          onClose={() => setShowAdvancedPDFExportModal(false)}
          onExport={exportAdvancedPDF}
          exportFilename={exportFilename}
          setExportFilename={setExportFilename}
        />
      )}
  
      {/* Modals */}
      <input
        type="file"
        accept=".groove,.json"
        style={{ display: 'none' }}
        ref={fileInputRef}
        onChange={loadStateFromJSON}
      />

      <input
        type="file"
        accept="application/pdf,.pdf"
        style={{ display: 'none' }}
        ref={pdfInputRef}
        onChange={handlePDFUpload}
      />

      <input
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        ref={imageInputRef}
        onChange={handleImageUpload}
      />
  
      
      <PageTabs
        pages={pages}
        activePage={activePage}
        handlePageClick={handlePageClick}
        handleDeleteClick={handleDeleteClick}
        handleAddPage={addPage}
        handleMovePage={handleMovePage}
      />
    
  
      {showImageLibraryModal && (
        <ImageLibraryModal
          onClose={() => {
            setShowImageLibraryModal(false);
            setIsSwapImageMode(false);
            setSwapTargetShapeId(null);
            setImageLibraryInitialCategory(null);
          }}
          onImageSelect={handleImageSelect}
          logoUrl={logoUrl}
          squareLogoUrl={squareLogoUrl}
          initialCategory={imageLibraryInitialCategory}
          isSwapMode={isSwapImageMode}
        />
      )}
  
      {showDeleteModal && (
        <DeleteModal deletePage={deletePage} closeModal={closeModal} />
      )}
  
      {showUploadModal && (
        <UploadTemplateModal
          onClose={() => {
            setShowUploadModal(false);
          }}
          onUpload={handleUploadTemplate}
          pages={pages}
          categories={categories}
          user={currentUser}
          editingTemplate={editingTemplate}
          existingTemplates={templates}
        />
      )}


  
      {showLastPageModal && <LastPageModal closeModal={closeModal} />}

      {isEditorTourOpen && (
        <Walkthrough
          steps={editorTourSteps}
          stepIndex={editorTourStep}
          onStepChange={setEditorTourStep}
          onClose={closeEditorTour}
          onFinish={finishEditorTour}
        />
      )}

      {isToolsTourOpen && (
        <Walkthrough
          steps={toolsTourSteps}
          stepIndex={toolsTourStep}
          onStepChange={setToolsTourStep}
          onClose={closeToolsTour}
          onFinish={finishToolsTour}
        />
      )}
  
      {/* Context Menu */}
      {contextMenuVisible && (
        <div
          ref={contextMenuRef}
          className="custom-context-menu"
          style={{
            top: contextMenuPosition.y,
            left: contextMenuPosition.x,
            position: 'fixed',
            backgroundColor: '#fff',
            border: '1px solid #ccc',
            zIndex: 1000,
            boxShadow: '0px 0px 5px rgba(0,0,0,0.2)',
          }}
        >
          <ul style={{ listStyle: 'none', margin: 0, padding: '5px 0' }}>
            <li
                          onClick={() => {
                            undo();
                            hideContextMenu();
                          }}
                          style={{ padding: '5px 20px', cursor: 'pointer', color: '#000' }}
                        >
                          <FontAwesomeIcon icon={faArrowLeft} style={{ marginRight: 10 }} />
                          Undo
                        </li>
                        <li
                          onClick={() => {
                            redo();
                            hideContextMenu();
                          }}
                          style={{ padding: '5px 20px', cursor: 'pointer', color: '#000' }}
                        >
                          <FontAwesomeIcon icon={faArrowRight} style={{ marginRight: 10 }} />
                          Redo
                        </li>
                        <li
                          onClick={() => {
                            bringToFront();
                            hideContextMenu();
                          }}
                          style={{
                            padding: '5px 20px',
                            cursor: selectedShapeIds.length > 0 ? 'pointer' : 'not-allowed',
                            color: selectedShapeIds.length > 0 ? '#000' : '#aaa',
                          }}
                        >
                          <FontAwesomeIcon icon={faArrowUp} style={{ marginRight: 10 }} />
                          Bring to Front
                        </li>
                        <li
                          onClick={() => {
                            bringForward();
                            hideContextMenu();
                          }}
                          style={{ padding: '5px 20px', cursor: 'pointer', color: '#000' }}
                        >
                          <FontAwesomeIcon icon={faArrowUp} style={{ marginRight: 10 }} />
                          Bring Forward
                        </li>
                        <li
                          onClick={() => {
                            sendToBack();
                            hideContextMenu();
                          }}
                          style={{
                            padding: '5px 20px',
                            cursor: selectedShapeIds.length > 0 ? 'pointer' : 'not-allowed',
                            color: selectedShapeIds.length > 0 ? '#000' : '#aaa',
                          }}
                        >
                          <FontAwesomeIcon icon={faArrowDown} style={{ marginRight: 10 }} />
                          Send to Back
                        </li>
                        <li
                          onClick={() => {
                            sendBack();
                            hideContextMenu();
                          }}
                          style={{ padding: '5px 20px', cursor: 'pointer', color: '#000' }}
                        >
                          <FontAwesomeIcon icon={faArrowDown} style={{ marginRight: 10 }} />
                          Send Back
                        </li>
                        <li
                          onClick={() => {
                            copySelectedShapes();
                            hideContextMenu();
                          }}
                          style={{
                            padding: '5px 20px',
                            cursor: selectedShapeIds.length > 0 ? 'pointer' : 'not-allowed',
                            color: selectedShapeIds.length > 0 ? '#000' : '#aaa',
                          }}
                        >
                          <FontAwesomeIcon icon={faCopy} style={{ marginRight: 10 }} />
                          Copy
                        </li>
                        <li
                          onClick={handleContextMenuPaste}
                          style={{
                            padding: '5px 20px',
                            cursor: 'pointer',
                            color: '#000',
                          }}              
                        >
            
                          <FontAwesomeIcon icon={faPaste} style={{ marginRight: 10 }} />
                          Paste
                        </li>
                        <li
                          onClick={handleContextMenuPasteInPlace}
                          style={{
                            padding: '5px 20px',
                            cursor: 'pointer',
                            color: '#000',
                          }}
                        >
                          <FontAwesomeIcon icon={faPaste} style={{ marginRight: 10 }} />
                          Paste in place
                        </li>

                        <li
                          onClick={() => {
                            deleteSelectedShapes();
                            hideContextMenu();
                          }}
                          style={{
                            padding: '5px 20px',
                            cursor: selectedShapeIds.length > 0 ? 'pointer' : 'not-allowed',
                            color: selectedShapeIds.length > 0 ? '#000' : '#aaa',
                          }}
                        >
                          <FontAwesomeIcon icon={faTrash} style={{ marginRight: 10 }} />
                          Delete
                        </li>
                      </ul>
        </div>
      )}
    </div>
  );
  
};



export default MainApp;
